import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import PuppeteerPDFService from './puppeteerPDFService';
import { ExportData } from '../utils/exportUtils';
import { Point } from '../types';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Client-side HTML to PDF conversion using jsPDF and html2canvas
async function convertHTMLToPDFBlob(html: string, filename: string): Promise<Blob> {
  try {
    console.log('🎨 Starting offline PDF generation...');
    
    // Create temporary container
    // IMPORTANT: Must be in viewport for html2canvas to capture images properly
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '210mm';
    container.style.minHeight = '297mm';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '-9999';
    document.body.appendChild(container);

    // CRITICAL: Give browser time to paint base64 images before html2canvas capture
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Wait for any images to load
    const images = container.getElementsByTagName('img');
    console.log(`📷 Found ${images.length} images in HTML, waiting for them to load...`);
    
    // Debug: Check for RIR diagram image
    const imageDetails = Array.from(images).map((img, index) => {
      const isBase64 = img.src.startsWith('data:image');
      const srcPreview = img.src.substring(0, 50);
      const detail = {
        index: index + 1,
        type: isBase64 ? 'Base64' : 'URL',
        srcLength: img.src.length,
        complete: img.complete,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        srcPreview
      };
      console.log(`📷 Image ${index + 1}:`, detail);
      return detail;
    });
    
    await Promise.all(Array.from(images).map((img, index) => {
      if (img.complete && img.naturalWidth > 0) {
        console.log(`✓ Image ${index + 1} already loaded (${img.naturalWidth}x${img.naturalHeight})`);
        return Promise.resolve();
      }
      console.log(`⏳ Waiting for image ${index + 1} to load...`);
      return new Promise((resolve) => {
        img.onload = () => {
          console.log(`✓ Image ${index + 1} loaded (${img.naturalWidth}x${img.naturalHeight})`);
          resolve(void 0);
        };
        img.onerror = () => {
          console.warn(`✗ Image ${index + 1} failed to load`);
          resolve(void 0);
        };
        setTimeout(() => {
          console.warn(`⏱ Image ${index + 1} load timeout`);
          resolve(void 0);
        }, 5000);
      });
    }));
    
    console.log('✅ All images processed, proceeding with conversion...');

    // Initialize PDF with enhanced quality settings
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
      precision: 8      // Moderate precision for better quality
    });

    // Get all pages
    const pages = container.querySelectorAll('.pdf-page');
    console.log(`📄 Found ${pages.length} pages to convert`);
    
    // Debug: Check for RIR page
    Array.from(pages).forEach((page, index) => {
      const heading = page.querySelector('.section-heading');
      const title = page.querySelector('.section-title');
      const pageTitle = heading?.textContent || title?.textContent || 'Unknown';
      console.log(`📄 Page ${index + 1}: ${pageTitle}`);
    });

    if (pages.length === 0) {
      throw new Error('No pages found in HTML content');
    }

    // Convert each page to canvas and add to PDF
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement;
      console.log(`🖼️ Converting page ${i + 1}/${pages.length}...`);

      try {
        // Generate ultra high-quality canvas (near maximum resolution)
        const canvas = await html2canvas(page, {
          scale: 3.75,           // Ultra resolution: 3.5 -> 3.75 for maximum quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: page.scrollWidth || page.offsetWidth,
          height: page.scrollHeight || page.offsetHeight,
          logging: false,
          imageTimeout: 10000,   // Reasonable timeout for complex content
          foreignObjectRendering: false,  // Disable to avoid document.write issues
          removeContainer: true,          // Clean up temporary elements
          windowWidth: page.scrollWidth || page.offsetWidth,
          windowHeight: page.scrollHeight || page.offsetHeight
        });

        // Add new page (except for first page)
        if (i > 0) {
          console.log(`📄 [PDF_PAGES_OFFLINE] Adding page ${i + 1} via html2canvas conversion`);
          pdf.addPage();
          console.log(`📄 [PDF_PAGES_OFFLINE] ✅ Page ${i + 1} added`);
        } else {
          console.log(`📄 [PDF_PAGES_OFFLINE] Using initial page for page 1`);
        }

        // Add image to PDF (working settings)
        const imgData = canvas.toDataURL('image/png', 1.0);
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
        
      } catch (pageError) {
        console.warn(`Failed to convert page ${i + 1}:`, pageError);
        // Add blank page to maintain pagination
        if (i > 0) pdf.addPage();
      }
    }

    // Clean up
    document.body.removeChild(container);
    
    // Convert to blob
    const pdfBlob = pdf.output('blob');
    const finalPageCount = pdf.getNumberOfPages();
    console.log('📄 [PDF_PAGES_OFFLINE] ======================================');
    console.log(`📄 [PDF_PAGES_OFFLINE] FINAL: Total pages generated: ${finalPageCount}`);
    console.log('📄 [PDF_PAGES_OFFLINE] ======================================');
    console.log('✅ PDF generated successfully');
    return pdfBlob;
    
  } catch (error) {
    console.error('❌ Failed to generate PDF:', error);
    throw error;
  }
}

function formatBuildingFabricText(text: string): string {
  if (!text || text === 'Not specified') return 'Not Specified';
  
  return text
    .replace(/[-_]/g, ' ') // Remove dashes and underscores
    .split(' ')
    .map(word => {
      if (word.length === 0) return '';
      // Handle special cases for ordinals (1st, 2nd, 3rd, etc.)
      if (/^\d+(st|nd|rd|th)$/.test(word.toLowerCase())) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      // Handle hyphens and special characters within words
      if (word.includes('-')) {
        return word.split('-').map(part => 
          part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        ).join('-');
      }
      // Standard capitalization
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

// Map internal wall type codes (e.g., 'CA') to human-readable labels (e.g., 'Cavity')
function mapWallTypeCodeToLabel(code?: string): string | undefined {
  if (!code) return undefined;
  const map: Record<string, string> = {
    SG: 'Stone (granite or whinstone)',
    SS: 'Stone (sandstone or limestone)',
    SO: 'Solid Brick',
    CO: 'Cob',
    CA: 'Cavity',
    TI: 'Timber Frame',
    SY: 'System Build',
    CW: 'Curtain Wall',
  };
  return map[code.toUpperCase()];
}

// Map RdSAP roof type codes to human terms
function mapRoofTypeCodeToLabel(code?: string): string | undefined {
  if (!code) return undefined;
  const c = code.toUpperCase();
  switch (c) {
    case 'PA':
    case 'PN':
      return 'Pitched';
    case 'PS':
      return 'Sloping Ceiling';
    case 'PT':
      return 'Thatch';
    case 'F':
      return 'Flat';
    case 'S':
      return 'Same Dwelling Above';
    case 'A':
      return 'Another Dwelling Above';
    case 'NR':
      return 'Non-residential Above';
    default:
      return undefined;
  }
}

// Map RdSAP ground level codes to human terms (for floor type)
function mapGroundLevelCodeToLabel(code?: string): string | undefined {
  if (!code) return undefined;
  const c = code.toUpperCase();
  switch (c) {
    case 'G':
      return 'Ground Floor';
    case 'P':
      return 'Above Partially Heated Space';
    case 'U':
      return 'Above Unheated Space';
    case 'E':
      return 'To External Air';
    case 'S':
      return 'Same Dwelling Below';
    case 'A':
      return 'Another Dwelling Below';
    default:
      return undefined;
  }
}

/**
 * Generate Room in Roof diagram page for PDF
 */
function generateRoomInRoofPage(siteNotesData: any, bestAddress: string, logoDataUrl?: string): string {
  const rirData = siteNotesData?.formData?.roomInRoofData;
  const hasRoomInRoof = siteNotesData?.formData?.hasRoomInRoof === 'yes';
  
  // Only generate RIR page if toggle is enabled AND data exists
  if (!hasRoomInRoof) {
    console.log('📐 Skipping Room in Roof page - toggle is not enabled');
    return '';
  }
  
  if (!rirData || !rirData.measurements || rirData.measurements.length === 0) {
    return '';
  }

  console.log('🎨 RIR DIAGRAM DEBUG:', {
    hasRirData: !!rirData,
    hasDiagramImage: !!rirData.diagramImage,
    diagramImageLength: rirData.diagramImage?.length,
    diagramImagePreview: rirData.diagramImage?.substring(0, 50),
    diagramType: rirData.diagramType,
    measurementsCount: rirData.measurements?.length
  });

  const diagramType = rirData.diagramType || 'standard';
  const diagramTitle = diagramType === 'with-dormer' ? 'Room in Roof with Dormer' : 'Standard Room in Roof';
  
  // Generate table rows for measurements
  const tableRows = rirData.measurements
    .filter((m: any) => m.visible !== false)
    .map((m: any) => `
      <tr>
        <td>${m.label}</td>
        <td>${m.length || '-'}</td>
        <td>${m.height || '-'}</td>
        <td>${m.insulation || '-'}</td>
        <td>${m.insulationType || '-'}</td>
      </tr>
    `).join('');

  const logoHtml = logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : '';

  return `
    <div class="pdf-page">
      <div class="header">
        <div class="header-content">
          <div class="header-left">
            <h1 class="property-address">${bestAddress}</h1>
            <p class="section-title">Room in Roof Assessment</p>
          </div>
          <div class="header-right">${logoHtml}</div>
        </div>
      </div>
      <div class="header-separator"></div>
      <div class="content-section">
        <h2 class="section-heading">${diagramTitle}</h2>
        
        <!-- Diagram Visualization -->
        <div style="width: 100%; max-width: 600px; margin: 16pt auto;">
          ${rirData.diagramImage ? `
            <img 
              src="${rirData.diagramImage}"
              alt="${diagramTitle}"
              style="display: block; width: 100%; height: auto; object-fit: contain; border: 1pt solid #E2E8F0; border-radius: 8pt;"
            />
          ` : '<p style="text-align: center; color: #64748B; padding: 32pt;">Diagram not available</p>'}
        </div>
        
        <div class="rir-diagram-section">
          <h3>Area Details</h3>
          <table class="rir-table">
            <thead>
              <tr>
                <th>Area</th>
                <th>Length (m)</th>
                <th>Height (m)</th>
                <th>Insulation</th>
                <th>Insulation Type</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

function generateBuildingFabricTableHTML(siteNotesData: any): string {
  // Extract building fabric data from the updated form structure
  // Prefer per-section values from wallConstructionSections (e.g., "Main" section),
  // with graceful fallback to legacy top-level fields.
  const wallSections = siteNotesData?.formData?.wallConstructionSections || [];
  const preferredSection = Array.isArray(wallSections) && wallSections.length > 0
    ? wallSections.find((s: any) => typeof s?.sectionLabel === 'string' && /main/i.test(s.sectionLabel)) || wallSections[0]
    : undefined;

  const wallTypeRaw =
    preferredSection?.wallType ||
    siteNotesData?.formData?.wallType ||
    undefined;

  const wallThicknessRaw =
    preferredSection?.wallThickness ||
    siteNotesData?.formData?.wallThickness;

  const roofTypeCode = siteNotesData?.formData?.roofTypeRdSAP;
  const roofTypeFallback = siteNotesData?.formData?.roofType;
  const groundLevelRaw = siteNotesData?.formData?.groundLevel || 'G';
  const floorTypeRaw = siteNotesData?.formData?.floorType || '';
  
  console.log('🚨 [BUILDING FABRIC] RAW DATA FROM SITE NOTES:', {
    roofTypeCode,
    roofTypeFallback,
    groundLevelRaw,
    floorTypeRaw,
    wallTypeRaw,
    wallThicknessRaw
  });
  
  // Format and clean up text
  const wallTypeLabelFromCode = mapWallTypeCodeToLabel(wallTypeRaw);
  const formattedWallType = wallTypeLabelFromCode
    ? wallTypeLabelFromCode
    : wallTypeRaw
      ? formatBuildingFabricText(wallTypeRaw)
      : 'Not Specified';

  const formattedWallCombined = formattedWallType + (wallThicknessRaw ? ` (${wallThicknessRaw}mm)` : '');

  const roofLabelFromCode = mapRoofTypeCodeToLabel(roofTypeCode);
  const formattedRoofType = roofLabelFromCode
    ? roofLabelFromCode
    : roofTypeFallback
      ? formatBuildingFabricText(roofTypeFallback)
      : 'Not Specified';

  // 🔧 FIX: Use groundLevel logic to determine floor type
  // If groundLevel is 'G', use floorType field
  // Otherwise, use the groundLevel code itself
  let formattedFloorType: string;
  if (groundLevelRaw === 'G' && floorTypeRaw) {
    // Ground floor - use specific floor type
    formattedFloorType = formatBuildingFabricText(floorTypeRaw);
  } else {
    // Not ground floor - use ground level description
    const groundLevelLabel = mapGroundLevelCodeToLabel(groundLevelRaw);
    formattedFloorType = groundLevelLabel || formatBuildingFabricText(groundLevelRaw);
  }
  
  console.log('🚨 [BUILDING FABRIC] FORMATTED VALUES FOR PDF:', {
    formattedWallType,
    formattedWallCombined,
    formattedRoofType,
    formattedFloorType,
    floorTypeSource: groundLevelRaw === 'G' ? 'floorType field' : 'groundLevel code'
  });

  return `
    <div class="building-fabric-container" style="margin: 8pt 0 12pt 0; position: relative;">
      <table class="building-fabric-table" style="
        width: 65%;
        margin: 0 auto;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: 6pt;
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        border-bottom: 1.5pt solid rgba(0, 0, 0, 0.12);
        border-right: 1pt solid rgba(0, 0, 0, 0.1);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <thead>
          <tr style="background: linear-gradient(135deg, #1E293B 0%, #334155 100%);">
            <th style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 8pt;
              font-weight: 500;
              border: none;
              width: 33.33%;
            ">.</th>
            <th style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 8pt;
              font-weight: 500;
              border: none;
              width: 33.33%;
            ">.</th>
            <th style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 8pt;
              font-weight: 500;
              border: none;
              width: 33.33%;
            ">.</th>
          </tr>
        </thead>
        <tbody>
          <tr style="background: #F8FAFC;">
            <td style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 7.5pt;
              border: 1pt solid #F1F5F9;
            ">.</td>
            <td style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 7.5pt;
              border: 1pt solid #F1F5F9;
            ">.</td>
            <td style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 7.5pt;
              border: 1pt solid #F1F5F9;
            ">.</td>
          </tr>
        </tbody>
      </table>
      
      <!-- Independently positioned text content -->
      <div style="position: absolute; top: -1pt; left: 17.5%; width: 21.67%; text-align: center; color: white; font-size: 8pt; font-weight: 500; display: table-cell; vertical-align: middle; height: 16pt;">Wall Type</div>
      <div style="position: absolute; top: -1pt; left: 39.17%; width: 21.67%; text-align: center; color: white; font-size: 8pt; font-weight: 500; display: table-cell; vertical-align: middle; height: 16pt;">Roof Type</div>
      <div style="position: absolute; top: -1pt; left: 60.83%; width: 21.67%; text-align: center; color: white; font-size: 8pt; font-weight: 500; display: table-cell; vertical-align: middle; height: 16pt;">Floor Type</div>
      
      <div style="position: absolute; top: 16pt; left: 17.5%; width: 21.67%; text-align: center; color: #1E293B; font-size: 7.5pt; display: table-cell; vertical-align: middle; height: 14pt;">${formattedWallCombined}</div>
      <div style="position: absolute; top: 16pt; left: 39.17%; width: 21.67%; text-align: center; color: #1E293B; font-size: 7.5pt; display: table-cell; vertical-align: middle; height: 14pt;">${formattedRoofType}</div>
      <div style="position: absolute; top: 16pt; left: 60.83%; width: 21.67%; text-align: center; color: #1E293B; font-size: 7.5pt; display: table-cell; vertical-align: middle; height: 14pt;">${formattedFloorType}</div>
    </div>
  `;
}

export async function exportToPDFOffline(data: ExportData, filename: string = 'property-report.pdf', options: { logoDataUrl?: string; headerColor?: string; companyName?: string; companyWebsite?: string; companyPhone?: string } = {}): Promise<void> {
  console.log('🚨🚨🚨 EXPORT START: exportToPDFOffline function called');
  
  // 🚨 DEBUG: Comprehensive analysis of incoming export data
  console.log('🔍 EXPORT SERVICE DEBUG - ROOM LABEL DATA RECEIVED:', {
    hasRoomLabelPositions: !!data.roomLabelPositions,
    roomLabelPositionsType: typeof data.roomLabelPositions,
    roomLabelPositionsCount: Object.keys(data.roomLabelPositions || {}).length,
    currentFloor: data.currentFloor,
    roomCount: data.rooms?.length || 0,
    roomCustomLabelsCount: Object.keys(data.roomCustomLabels || {}).length,
    allFloorPlansKeys: Object.keys(data.allFloorPlans || {}),
    roomLabelPositionsSample: data.roomLabelPositions ? 
      Object.entries(data.roomLabelPositions).slice(0, 3).map(([key, pos]) => ({
        key: key.substring(0, 15) + '...',
        x: (pos as any)?.x?.toFixed(1),
        y: (pos as any)?.y?.toFixed(1)
      })) : 'NONE - THIS IS THE ISSUE!'
  });

  console.log('🖼️ LOGO DEBUG: exportToPDFOffline called with options:', {
    hasLogo: !!options.logoDataUrl,
    logoDataLength: options.logoDataUrl?.length || 0,
    filename
  });
  // Global styles (shared across all pages)
  const globalStyles = `
    <style>
      @page { margin: 0; }
      .pdf-page {
        page-break-after: always;
        height: 297mm;
        margin: 0;
        padding: 5mm;
        font-family: 'Helvetica', 'Arial', sans-serif;
        font-size: 10pt;
        color: #1E293B;
        background: white;
        box-sizing: border-box;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
      }

      .header {
        background: white;
        border: 0.5pt solid #E5E7EB;
        border-radius: 8pt;
        padding: 8pt;
        margin-bottom: 6pt;
        box-shadow: 0.3pt 0.3pt 0pt #E5E7EB;
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .header-left {
        display: flex;
        flex-direction: column;
        gap: 2pt;
      }

      .header-right {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .header-logo {
        max-height: 32pt;
        max-width: 80pt;
        height: auto;
        width: auto;
        object-fit: contain;
      }

      .page-number {
        font-size: 8pt;
        color: #6B7280;
        font-weight: normal;
        margin: 0;
      }

      .property-address {
        font-size: 11pt;
        font-weight: 600;
        color: #111827;
        margin: 0 0 2pt 0;
      }

      .section-title {
        font-size: 9pt;
        color: #374151;
        margin: 0;
      }

      .header-separator {
        height: 0.3pt;
        background: #E5E7EB;
        margin: 6pt 0;
      }

      .content-section {
        margin-bottom: 16pt;
      }

      .section-heading {
        font-size: 11pt;
        font-weight: 400;
        color: #1E293B;
        margin: 0 0 8pt 0;
        padding: 0 12pt;
        background: #F8FAFC;
        border-left: 3pt solid #3B82F6;
        border-radius: 4pt;
        text-transform: uppercase;
        letter-spacing: 0.3pt;
        position: relative;
        width: 100%;
        box-sizing: border-box;
        height: 24pt;
        display: table-cell;
        vertical-align: middle;
        text-align: left;
      }

      /* First page compaction to ensure fit */
      .first-page .header { padding: 6pt; margin-bottom: 5pt; }
      .first-page .header-separator { margin: 4pt 0; }
      .first-page .section-heading { margin: 0 0 6pt 0; padding-bottom: 3pt; }
      .first-page .property-address { font-size: 10.5pt; }
      .pdf-page .content-section:last-child { margin-bottom: 0; }

      /* Enhanced sizing with more floor plan space */
      .pdf-page {
        --page-height: 297mm;      /* A4 height */
        --page-vpad: 10mm;         /* 5mm top + 5mm bottom padding */
        --header-est: 22mm;        /* reduced header estimate */
        --legend-est: 140mm;       /* reduced legend estimate for more floor plan space */
        --available-height: calc(var(--page-height) - var(--page-vpad) - var(--header-est) - var(--legend-est));
      }
      
      .floorplan-image {
        max-height: min(230mm, var(--available-height)) !important;
        height: auto !important;
      }
      
      /* Full-page floor plan with space for section heading and Building Fabric table */
      .floorplan-image-fullpage {
        max-width: 200mm !important;
        max-height: 200mm !important;
        height: auto !important;
        width: auto !important;
        display: block;
        margin: 0 auto;
      }
      
      /* Ensure first page uses same constraints */
      .first-page .floorplan-image {
        max-height: min(230mm, var(--available-height)) !important;
      }

      /* Proper layout flow to ensure element key appears below floor plan */
      .pdf-page { 
        display: block; 
        position: relative;
        min-height: 297mm; 
      }
      
      .pdf-page .floorplan-section { 
        display: block;
        margin-bottom: 12pt;
        break-inside: avoid-page;
        page-break-inside: avoid;
      }

      /* Keep legend attached to the image section when possible */
      .pdf-page .floorplan-section + .content-section.avoid-break {
        break-before: avoid-page;
        page-break-before: avoid;
      }
      
      .pdf-page .floorplan-section { 
        display: flex;
        flex-direction: column;
        align-items: stretch;
        margin-bottom: 12pt;
        break-inside: avoid-page;
        page-break-inside: avoid;
      }
      
      .pdf-page .floorplan-section .floorplan-image { 
        display: block;
        max-height: min(200mm, var(--available-height)) !important; /* adaptive cap with hard ceiling */
        max-width: 100%;
        width: auto !important;
        height: auto !important;
        object-fit: contain;
        margin: 0 auto;
      }

      /* Keep the legend unbreakable but do not enforce break-after on the image section */
      
      .pdf-page .element-key { 
        display: block;
        margin-top: 12pt;
        margin-bottom: 4pt;
        position: relative;
        z-index: 1;
        break-inside: avoid-page;
        page-break-inside: avoid;
      }

      /* Prevent legend rows/cells from splitting across pages in Chromium */
      .element-key,
      .element-key .ek-row,
      .element-key .ek-cell {
        break-inside: avoid-page;
        page-break-inside: avoid;
      }

      .floorplan-image {
        max-width: 100%;
        width: auto;
        height: auto;
        display: block;
        object-fit: contain;
        image-rendering: auto;
        margin: 0 auto;
      }

      .avoid-break {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .element-key {
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        border-bottom: 1.5pt solid rgba(0, 0, 0, 0.12);
        border-right: 1pt solid rgba(0, 0, 0, 0.1);
        border-radius: 12pt;
        overflow: hidden;
        margin-top: 16pt;
        background: #FFFFFF;
      }

      .element-key .ek-header {
        background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
        color: #FFFFFF;
        font-weight: 500;
        text-align: center;
        padding: 7pt 16pt 13pt 16pt;
        font-size: 10pt;
        letter-spacing: 0.5pt;
        text-transform: uppercase;
        border-bottom: none;
        position: relative;
      }
      
      .element-key .ek-header::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2pt;
        background: linear-gradient(90deg, #3B82F6, #60A5FA);
      }

      .element-key .ek-row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        border-bottom: 1pt solid #E8EDF3;
        transition: background-color 0.2s ease;
      }
      
      .element-key .ek-row:nth-child(even) {
        background: #FAFBFC;
      }

      .element-key .ek-row:last-child {
        border-bottom: none;
      }

      .element-key .ek-cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8pt;
        padding: 8pt 12pt;
        border-right: 1pt solid #E8EDF3;
        height: 32pt;
        position: relative;
        box-sizing: border-box;
      }

      .element-key .ek-cell:last-child {
        border-right: none;
      }
      
      .element-key .ek-cell:hover {
        background: rgba(59, 130, 246, 0.04);
      }

      .ek-symbol-line {
        display: inline-block;
        width: 14pt;
        height: 0;
        border-top: 1.2pt solid #000;
      }

      .ek-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 1pt 6pt;
        font-size: 7pt;
        font-weight: 500;
        border: 1pt solid rgba(0,0,0,0.1);
        border-radius: 6pt;
        min-width: 20pt;
        height: 18pt;
        flex-shrink: 0;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
      }

      .ek-light-led {
        color: #22c55e;
        font-weight: 500;
        font-size: 9pt;
        line-height: 1;
      }

      .ek-light-standard {
        display: inline-block;
        width: 7pt;
        height: 7pt;
        border: 1.2pt solid #f97316;
        border-radius: 50%;
      }

      .ek-desc {
        font-size: 8pt;
        color: #64748B;
        font-weight: 400;
        line-height: 1.3;
      }
      
      .ek-desc strong {
        color: #334155;
        font-weight: 500;
      }

      .ek-icon-circle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 7pt;
        font-weight: 700;
        color: #ffffff;
        width: 18pt;
        height: 18pt;
        border-radius: 50%;
        border: 1pt solid rgba(0,0,0,0.15);
        flex-shrink: 0;
        -webkit-font-smoothing: antialiased;
      }

      .ek-icon-square {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 9pt;
        color: #ffffff;
        width: 18pt;
        height: 18pt;
        border-radius: 3pt;
        border: 1pt solid rgba(0,0,0,0.15);
        flex-shrink: 0;
      }

      .ek-icon-rect {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 0 4pt;
        font-size: 7pt;
        font-weight: 600;
        color: #ffffff;
        min-width: 22pt;
        height: 18pt;
        border-radius: 3pt;
        border: 1pt solid rgba(0,0,0,0.15);
        flex-shrink: 0;
        -webkit-font-smoothing: antialiased;
      }

      /* Room in Roof Table Styles */
      .rir-diagram-section {
        margin: 12pt 0;
      }

      .rir-diagram-section h3 {
        font-size: 10pt;
        font-weight: 600;
        color: #1E293B;
        margin-bottom: 8pt;
      }

      .rir-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 8pt;
        margin: 8pt 0;
      }

      .rir-table thead {
        background-color: #F1F5F9;
      }

      .rir-table th {
        padding: 6pt 8pt;
        text-align: left;
        font-weight: 600;
        color: #475569;
        border: 1pt solid #CBD5E1;
      }

      .rir-table tbody tr {
        border-bottom: 1pt solid #E2E8F0;
      }

      .rir-table tbody tr:nth-child(even) {
        background-color: #F8FAFC;
      }

      .rir-table tbody tr:hover {
        background-color: #F1F5F9;
      }

      .rir-table td {
        padding: 5pt 8pt;
        border: 1pt solid #E2E8F0;
        color: #1E293B;
      }

      .rir-table td:first-child {
        font-weight: 500;
      }

      @media print {
        .pdf-page {
          margin: 0;
          box-shadow: none;
          page-break-after: always;
        }
        .pdf-page:last-child {
          page-break-after: auto;
        }
      }

      /* OFFLINE EXPORT: Property Overview & Floor Calculations Section Styling (carbon copy from server) */
      .overview-grid {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 6pt !important;
        margin-bottom: 12pt !important;
      }

      .overview-item {
        display: flex !important;
        justify-content: space-between !important;
        padding: 4pt 8pt !important;
        background: #F8FAFC !important;
        border-radius: 4pt !important;
        border: 0.5pt solid #E2E8F0 !important;
      }

      .overview-label {
        font-weight: 500 !important;
        color: #475569 !important;
      }

      .overview-value {
        font-weight: bold !important;
        color: #1E293B !important;
      }

      /* Modern Calculations Table */
      .modern-table-container {
        background: #FFFFFF !important;
        border-radius: 12pt !important;
        box-shadow: 0 2pt 8pt rgba(0, 0, 0, 0.1) !important;
        overflow: hidden !important;
        margin: 8pt 0 !important;
        border: 1pt solid #E5E7EB !important;
      }

      .modern-calc-table {
        width: 100% !important;
        border-collapse: separate !important;
        border-spacing: 0 !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        font-size: 10pt !important;
      }

      .modern-calc-table thead tr {
        background: ${options.headerColor || '#F59E0B'} !important;
      }

      .modern-calc-table th.floor-column {
        color: white !important;
        font-size: 11pt !important;
        font-weight: bold !important;
        text-align: center !important;
        padding: 8pt 12pt !important;
        width: 40% !important;
        border-top-left-radius: 12pt !important;
      }

      .modern-calc-table th.data-column {
        color: white !important;
        font-size: 10pt !important;
        font-weight: bold !important;
        text-align: center !important;
        padding: 8pt 6pt !important;
        width: 15% !important;
      }

      .modern-calc-table th.data-column:last-child {
        border-top-right-radius: 12pt !important;
      }

      .modern-calc-table .table-row {
        transition: background-color 0.2s ease !important;
      }

      .modern-calc-table .even-row {
        background: #F8FAFC !important;
      }

      .modern-calc-table .odd-row {
        background: #FFFFFF !important;
      }

      .modern-calc-table .table-row:hover {
        background: #FEF3C7 !important;
      }

      .modern-calc-table .floor-name {
        padding: 8pt 12pt !important;
        border: none !important;
        border-bottom: 0.5pt solid #E5E7EB !important;
        vertical-align: middle !important;
      }

      .modern-calc-table .floor-badge {
        display: inline-block !important;
        color: #1E293B !important;
        padding: 3pt 8pt !important;
        background: #EEF2FF !important;
        border-radius: 6pt !important;
        font-weight: 600 !important;
        font-size: 8pt !important;
        letter-spacing: 0.5pt !important;
      }

      .modern-calc-table .metric-value {
        padding: 8pt !important;
        text-align: center !important;
        font-weight: 500 !important;
        color: #334155 !important;
        border: none !important;
        border-bottom: 0.5pt solid #E5E7EB !important;
        vertical-align: middle !important;
        font-size: 9pt !important;
      }

      .modern-calc-table tbody tr:last-child .floor-name,
      .modern-calc-table tbody tr:last-child .metric-value {
        border-bottom: none !important;
      }

      /* Ensure table doesn't break page layout */
      .modern-calc-table,
      .modern-table-container {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

      /* Formula & Working Section Styling */
      .calculations-container {
        margin: 16pt 0 !important;
      }

      .floor-calculation-block {
        margin-bottom: 16pt !important;
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }

      .floor-header {
        background: ${options.headerColor || '#F59E0B'} !important;
        color: white !important;
        padding: 8pt 16pt !important;
        font-weight: 600 !important;
        font-size: 11pt !important;
        border-radius: 6pt 6pt 0 0 !important;
        margin: 0 !important;
      }

      .calculation-content {
        background: #F8FAFC !important;
        padding: 12pt 16pt !important;
        border: 1pt solid #E5E7EB !important;
        border-top: none !important;
        border-radius: 0 0 6pt 6pt !important;
      }

      .calc-item {
        margin-bottom: 6pt !important;
        display: flex !important;
        align-items: flex-start !important;
      }

      .calc-item:last-child {
        margin-bottom: 0 !important;
      }

      .calc-label {
        font-weight: 600 !important;
        color: #374151 !important;
        min-width: 50pt !important;
        margin-right: 12pt !important;
        font-size: 10pt !important;
        flex-shrink: 0 !important;
      }

      .calc-formula {
        color: #1F2937 !important;
        font-size: 9pt !important;
        font-family: 'Courier New', monospace !important;
        flex: 1 !important;
        word-break: break-all !important;
        line-height: 1.3 !important;
      }

      /* Page break utilities for pagination */
      .page-break-before {
        page-break-before: always !important;
        break-before: page !important;
      }

      .page-break-after {
        page-break-after: always !important;
        break-after: page !important;
      }

      /* Ensure floor calculation blocks stay together */
      .floor-calculation-block {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

    </style>
  `;

  // Helper function to generate element key HTML
  const getElementKeyHTML = (): string => {
    return `
      <div class="element-key">
        <div class="ek-header">ELEMENT KEY</div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <span class="ek-symbol-line" style="border-top-color:#ff0000"></span>
              <span class="ek-desc"><strong>HLP</strong> - Heat Loss Perimeter</span>
            </div>
            <div class="ek-cell">
              <span class="ek-symbol-line" style="border-top-color:#0000ff"></span>
              <span class="ek-desc"><strong>Party Wall</strong> - Party Wall</span>
            </div>
            <div class="ek-cell">
              <span class="ek-symbol-line" style="border-top-color:#f97316"></span>
              <span class="ek-desc"><strong>Sheltered</strong> - Sheltered Wall</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#fde047;">RADT</span>
              <span class="ek-desc">- Radiator with TRVs</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <span class="ek-badge" style="background:#fde047;">RAD</span>
              <span class="ek-desc">- Radiator</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#fde047;">H/S</span>
              <span class="ek-desc">- Heat Source</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#fde047;">PH</span>
              <span class="ek-desc">- Panel Heat</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#fde047;">FH</span>
              <span class="ek-desc">- Fan Heat</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <span class="ek-badge" style="background:#fde047;">ESH</span>
              <span class="ek-desc">- Storage Heat</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">GM</span>
              <span class="ek-desc">- Gas Meter</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">EM</span>
              <span class="ek-desc">- Electric Meter</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">CU</span>
              <span class="ek-desc">- Consumer Unit</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">TNK</span>
              <span class="ek-desc">- Tank</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">FAN</span>
              <span class="ek-desc">- Fan</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">V</span>
              <span class="ek-desc">- Vent</span>
            </div>
            <div class="ek-cell">
              <span class="ek-badge" style="background:#bbf7d0;">T-STAT</span>
              <span class="ek-desc">- Thermostat</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <span class="ek-badge" style="background:#e6f3ff;">LH</span>
              <span class="ek-desc">- Loft Hatch</span>
            </div>
            <div class="ek-cell">
              <span class="ek-light-led">×</span>
              <span class="ek-desc">- LED Light</span>
            </div>
            <div class="ek-cell">
              <span class="ek-light-standard"></span>
              <span class="ek-desc">- Standard Light</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc" style="color:#ef4444;">SA</strong>
              <span class="ek-desc">- Smoke Alarm</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <strong class="ek-desc">DR</strong>
              <span class="ek-desc">- Dining Room</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">ST</strong>
              <span class="ek-desc">- Study</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">BR</strong>
              <span class="ek-desc">- Bedroom</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">BTH</strong>
              <span class="ek-desc">- Bathroom</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <strong class="ek-desc">LR</strong>
              <span class="ek-desc">- Living Room</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">H</strong>
              <span class="ek-desc">- Hallway</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">L</strong>
              <span class="ek-desc">- Landing</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">K/D</strong>
              <span class="ek-desc">- Kitchen/Diner</span>
            </div>
          </div>
          
          <div class="ek-row">
            <div class="ek-cell">
              <strong class="ek-desc">K</strong>
              <span class="ek-desc">- Kitchen</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">W/C</strong>
              <span class="ek-desc">- Water Closet</span>
            </div>
            <div class="ek-cell">
              <strong class="ek-desc">CPD</strong>
              <span class="ek-desc">- Cupboard</span>
            </div>
            <div class="ek-cell"></div>
          </div>
        </div>`;
  };

  // Helper to match address formatting with existing exports
  const getBestAddress = (propAddress?: string, siteAddress?: any) => {
    if (siteAddress) {
      if (typeof siteAddress === 'object' && siteAddress.houseNumber) {
        // Build address parts including optional buildingName
        const parts = [];
        if (siteAddress.buildingName && siteAddress.buildingName.trim()) {
          parts.push(siteAddress.buildingName.trim());
        }
        parts.push(`${siteAddress.houseNumber} ${siteAddress.street}`);
        parts.push(siteAddress.town);
        parts.push(siteAddress.postcode);
        return parts.filter(p => p && p.trim()).join(', ');
      }
      if (typeof siteAddress === 'string' && siteAddress.trim() !== '') {
        return siteAddress;
      }
    }
    return propAddress || 'Property Assessment Report';
  };

  // 1) Calculate ACTUAL floor plan pages BEFORE generating content HTML (needed for correct page numbering)
  // Must account for floors that need 2 pages (full-page layout with separate element key page)
  const allFloorPlans = data.allFloorPlans || { [data.currentFloor]: data.currentWalls };
  const availableFloorPlanFloors = Object.keys(allFloorPlans).map(Number).sort((a, b) => a - b);
  
  // Calculate actual page count by checking which floors need full-page layout
  let actualFloorPlanPageCount = 0;
  const AVAILABLE_WIDTH_MM = 180;
  const STANDARD_PAGE_HEIGHT_MM = 200;
  const STRETCHING_THRESHOLD = 0.15;
  
  for (const floorNumber of availableFloorPlanFloors) {
    const floorData = data.allFloorData?.[floorNumber];
    const floorWalls = floorData?.walls || (floorNumber === data.currentFloor ? data.currentWalls : allFloorPlans[floorNumber] || []);
    
    // Skip if no walls
    if (!floorWalls || floorWalls.length === 0) {
      continue;
    }
    
    // Calculate canvas dimensions (same logic as in main loop)
    const bounds = floorWalls.reduce((acc: any, wall: any) => {
      const x1 = wall.x1 ?? wall.start?.x;
      const y1 = wall.y1 ?? wall.start?.y;
      const x2 = wall.x2 ?? wall.end?.x;
      const y2 = wall.y2 ?? wall.end?.y;
      if (x1 !== undefined) {
        acc.minX = Math.min(acc.minX, x1, x2);
        acc.maxX = Math.max(acc.maxX, x1, x2);
        acc.minY = Math.min(acc.minY, y1, y2);
        acc.maxY = Math.max(acc.maxY, y1, y2);
      }
      return acc;
    }, { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });
    
    const padding = 50;
    const canvasWidth = (bounds.maxX - bounds.minX) + (padding * 2);
    const canvasHeight = (bounds.maxY - bounds.minY) + (padding * 2);
    const canvasWidthMm = canvasWidth * 0.264583;
    const canvasHeightMm = canvasHeight * 0.264583;
    
    // Check if needs full-page layout
    const standardWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
    const standardHeightScale = STANDARD_PAGE_HEIGHT_MM / canvasHeightMm;
    const scaleDifference = Math.abs(standardWidthScale - standardHeightScale);
    const scaleRatio = Math.max(standardWidthScale, standardHeightScale) / Math.min(standardWidthScale, standardHeightScale);
    const wouldBeStretched = scaleDifference > STRETCHING_THRESHOLD * Math.min(standardWidthScale, standardHeightScale) || scaleRatio > 1.3;
    
    if (wouldBeStretched) {
      actualFloorPlanPageCount += 2; // Full-page layout: floor plan page + element key page
    } else {
      actualFloorPlanPageCount += 1; // Standard layout: both on one page
    }
  }
  
  const floorPlanPageCount = actualFloorPlanPageCount;
  console.log(`📊 FLOOR PLAN PAGE COUNT for page numbering: ${floorPlanPageCount} (${availableFloorPlanFloors.length} floors, accounting for full-page layouts)`);
  
  // 2) Build base HTML from existing generator and extract sections for reordering
  const rawHtml = await PuppeteerPDFService.generateHTMLFromData(data, options.logoDataUrl, options.headerColor, options.companyName, options.companyWebsite, options.companyPhone, floorPlanPageCount);
  
  // Extract original dimensions table and add totals row
  const originalDimensionsMatch = rawHtml.match(
    /<div class="content-section">\s*<h2 class="section-heading">All Floors - Dimensions & Calculations<\/h2>[\s\S]*?<\/div>\s*<\/div>/
  );
  
  const addTotalsRowToTable = (originalTable: string, data: ExportData): string => {
    if (!originalTable) return '';
    
    const { siteNotesData, allFloorData } = data;
    if (!siteNotesData?.floors) return originalTable;
    
    // Calculate totals from live floor data
    let totalArea = 0;
    let totalFloors = 0;
    
    // Count floors from actual floor plans in editor 
    console.log(`📊 DEBUG: allFloorData available:`, !!allFloorData);
    console.log(`📊 DEBUG: allFloorData keys:`, allFloorData ? Object.keys(allFloorData) : 'none');
    
    if (allFloorData && Object.keys(allFloorData).length > 0) {
      totalFloors = Object.keys(allFloorData).length;
      console.log(`📊 FLOOR COUNT: ${totalFloors} floor plans found in editor`);
    } else {
      // Fallback: count unique floor IDs from siteNotesData
      const uniqueFloorIds = new Set();
      siteNotesData.floors.forEach((floor: any) => {
        if (floor.id && floor.floorLevel && floor.buildingType === 'main building') {
          uniqueFloorIds.add(floor.id);
        }
      });
      totalFloors = uniqueFloorIds.size;
      console.log(`📊 FLOOR COUNT (FALLBACK): ${totalFloors} main building floors found in siteNotesData`);
    }
    
    // Sum areas from site notes data (calculations)
    siteNotesData.floors.forEach((floor: any) => {
      const floorArea = parseFloat(floor.floorArea) || 0;
      if (floorArea > 0) {
        totalArea += floorArea;
      }
    });
    
    console.log(`📊 TOTALS CALCULATED: ${totalFloors} floors, ${totalArea.toFixed(2)}m² total area`);
    
    // Create totals row with original table styling and proper visibility
    const totalsRow = `
      <tr class="totals-row" style="background-color: #f8f9fa; border-top: 2px solid #dee2e6; font-weight: bold;">
        <td style="padding: 12px 8px; font-weight: bold;"><strong>Total (${totalFloors} floors)</strong></td>
        <td style="padding: 12px 8px; font-weight: bold;"><strong>${totalArea.toFixed(2)} m²</strong></td>
        <td style="padding: 12px 8px;">-</td>
        <td style="padding: 12px 8px;">-</td>
      </tr>
    `;
    
    // Insert totals row before closing </tbody> or </table>
    if (originalTable.includes('</tbody>')) {
      return originalTable.replace('</tbody>', `${totalsRow}</tbody>`);
    } else if (originalTable.includes('</table>')) {
      return originalTable.replace('</table>', `${totalsRow}</table>`);
    }
    
    return originalTable;
  };
  
  const dimensionsTableSection = originalDimensionsMatch 
    ? addTotalsRowToTable(originalDimensionsMatch[0], data)
    : '';
  
  // Remove unwanted sections and also remove the dimensions table from its original position
  // Also remove the page breaks that create blank pages and HLP/PW calculation summaries
  const baseHtml = rawHtml
    .replace(
      /<div class="content-section">\s*<h2 class="section-heading">Property Overview<\/h2>[\s\S]*?<\/div>\s*<\/div>/g,
      ''
    )
    .replace(
      /<div class="content-section">\s*<h2 class="section-heading">Property Overview & Floor Calculations<\/h2>[\s\S]*?<\/div>\s*<\/div>/g,
      ''
    )
    .replace(
      /<div class="pdf-page"[\s\S]*?Property Overview[\s\S]*?<\/div>\s*(?=<div class="pdf-page"|$)/g,
      ''
    )
    .replace(
      /<div class="content-section">\s*<h2 class="section-heading">All Floors - Dimensions & Calculations<\/h2>[\s\S]*?<\/div>\s*<\/div>/g,
      ''
    )
    .replace(
      /<div class="content-section">\s*<h2 class="section-heading">Formula & Working - All Floors<\/h2>[\s\S]*?<\/div>\s*<\/div>/g,
      ''
    )
    .replace(
      /<div class="page-break"><\/div>/g,
      ''
    );

  // 2) Generate floor plan sections for all floors
  // Track cumulative page numbers across all sections
  let currentPageNum = 1;
  let totalPages = 0; // Will calculate after determining page counts
  
  console.log('📄 [PDF_PAGES_OFFLINE] ======================================');
  console.log('📄 [PDF_PAGES_OFFLINE] Starting PDF generation with page tracking');
  console.log('📄 [PDF_PAGES_OFFLINE] Initial page:', currentPageNum);
  let floorPlanSection = '';
  
  try {
    const { generateEnhancedFloorPlanCanvas } = await import('../utils/exportUtils');
    const { siteNotesData, currentWalls, elements, rooms, textBoxes } = data;
    
    if (siteNotesData?.floors && siteNotesData.floors.length > 0) {
      // 🔧 EXTENSION FIX: Use same logic as jsPDF exportComprehensivePNG
      // Only generate floor plan pages for floors with actual floor plan data
      // Highlighter extensions are included in calculations but not as separate pages
      const allFloorPlans = data.allFloorPlans || { [data.currentFloor]: currentWalls };
      const allFloorData = data.allFloorData || {};
      const availableFloors = Object.keys(allFloorPlans).map(Number).sort((a, b) => a - b);
      
      console.log(`🏢 SERVER PDF: Found ${siteNotesData.floors.length} total site notes floors, generating ${availableFloors.length} floor plan pages`);
      console.log(`🔍 SERVER PDF: allFloorPlans keys:`, Object.keys(allFloorPlans));
      console.log(`🔍 SERVER PDF: allFloorData keys:`, Object.keys(allFloorData));
      console.log(`🔍 SERVER PDF: availableFloors:`, availableFloors);
      console.log(`🔍 SERVER PDF: currentFloor:`, data.currentFloor);
      console.log(`🔍 SERVER PDF: Note: Highlighter extensions included in calculations but not as separate pages`);
      
      // Calculate universal dimensions for consistent scaling across all floors
      let universalMaxWidth = 0;
      let universalMaxHeight = 0;
      
      console.log(`📐 CALCULATING UNIVERSAL DIMENSIONS for ${availableFloors.length} floors...`);
      
      // Pre-calculate bounds for all floors to determine universal scaling
      for (const floorNum of availableFloors) {
        const floorDataFromAll = allFloorData[floorNum];
        const preFloorWalls = floorDataFromAll?.walls || (floorNum === data.currentFloor ? currentWalls : allFloorPlans[floorNum] || []);
        const preFloorElements = floorDataFromAll?.elements || elements.filter((el: any) => el.floor === floorNum);
        
        if (preFloorWalls.length > 0 || preFloorElements.length > 0) {
          // Calculate bounds for this floor
          const allItems = [...preFloorWalls, ...preFloorElements];
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          
          allItems.forEach((item: any) => {
            if (item.start && item.end) {
              // Wall
              minX = Math.min(minX, item.start.x, item.end.x);
              maxX = Math.max(maxX, item.start.x, item.end.x);
              minY = Math.min(minY, item.start.y, item.end.y);
              maxY = Math.max(maxY, item.start.y, item.end.y);
            } else if (item.position) {
              // Element
              minX = Math.min(minX, item.position.x);
              maxX = Math.max(maxX, item.position.x);
              minY = Math.min(minY, item.position.y);
              maxY = Math.max(maxY, item.position.y);
            }
          });
          
          const floorWidth = maxX - minX;
          const floorHeight = maxY - minY;
          
          universalMaxWidth = Math.max(universalMaxWidth, floorWidth);
          universalMaxHeight = Math.max(universalMaxHeight, floorHeight);
          
          console.log(`📐 Floor ${floorNum} bounds: ${floorWidth.toFixed(1)}×${floorHeight.toFixed(1)}`);
        }
      }
      
      console.log(`📐 UNIVERSAL DIMENSIONS: ${universalMaxWidth.toFixed(1)}×${universalMaxHeight.toFixed(1)}`);
      
      // PRE-CALCULATE: Determine which floors need full-page layout to count total pages
      const floorPageCounts: number[] = [];
      for (const floorNum of availableFloors) {
        const floorDataFromAll = allFloorData[floorNum];
        const preFloorWalls = floorDataFromAll?.walls || (floorNum === data.currentFloor ? currentWalls : allFloorPlans[floorNum] || []);
        const preFloorElements = floorDataFromAll?.elements || elements.filter((el: any) => el.floor === floorNum);
        
        if (preFloorWalls.length === 0 && preFloorElements.length === 0) {
          floorPageCounts.push(0);
          continue;
        }
        
        // Calculate if this floor needs full-page layout (same logic as below)
        const allItems = [...preFloorWalls, ...preFloorElements];
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        allItems.forEach((item: any) => {
          if (item.start && item.end) {
            minX = Math.min(minX, item.start.x, item.end.x);
            maxX = Math.max(maxX, item.start.x, item.end.x);
            minY = Math.min(minY, item.start.y, item.end.y);
            maxY = Math.max(maxY, item.start.y, item.end.y);
          } else if (item.position) {
            minX = Math.min(minX, item.position.x);
            maxX = Math.max(maxX, item.position.x);
            minY = Math.min(minY, item.position.y);
            maxY = Math.max(maxY, item.position.y);
          }
        });
        
        const floorWidth = maxX - minX;
        const floorHeight = maxY - minY;
        const MM_TO_PX = 3.7795;
        const AVAILABLE_WIDTH_MM = 200;
        const STANDARD_PAGE_HEIGHT_MM = 230;
        
        const canvasWidthMm = floorWidth / MM_TO_PX;
        const canvasHeightMm = floorHeight / MM_TO_PX;
        const standardWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
        const standardHeightScale = STANDARD_PAGE_HEIGHT_MM / canvasHeightMm;
        
        // Use the SAME logic as actual page generation (line ~1980-2040)
        // Check if aspect ratio would cause stretching (wouldBeStretched in actual generation)
        const scaleDifference = Math.abs(standardWidthScale - standardHeightScale);
        const scaleRatio = standardWidthScale / standardHeightScale;
        const wouldBeStretched = scaleRatio > 1.2 || scaleRatio < 0.8;
        const needsFullPage = wouldBeStretched;
        
        console.log(`📊 FLOOR ${floorNum} PAGE PREDICTION:`, {
          canvasSize: `${canvasWidthMm.toFixed(1)}×${canvasHeightMm.toFixed(1)}mm`,
          scaleRatio: scaleRatio.toFixed(3),
          wouldBeStretched,
          needsFullPage,
          predictedPages: needsFullPage ? 2 : 1
        });
        
        floorPageCounts.push(needsFullPage ? 2 : 1);
      }
      
      // Calculate total pages:
      // Floor plan pages + calculations pages + content pages from puppeteerPDFService
      const totalFloorPlanPages = floorPageCounts.reduce((sum, count) => sum + count, 0);
      const calculationsPages = Math.ceil(siteNotesData.floors.length / 3); // 3 floors per calculations page
      
      // Content pages from puppeteerPDFService (after Property Overview and calculations):
      let contentPages = 0;
      contentPages++; // Equipment Summary (Radiators & Windows) - always present
      
      // Wall Insulation - conditional
      if (siteNotesData?.formData?.wallInsulationType || 
          (siteNotesData?.formData?.wallInsulationElevations && siteNotesData.formData.wallInsulationElevations.length > 0)) {
        contentPages++;
      }
      
      // Room in Roof - conditional (always count if hasRoomInRoof is yes, regardless of measurements)
      if (siteNotesData?.formData?.hasRoomInRoof === 'yes') {
        contentPages++;
      }
      
      contentPages++; // Property & Heating Information - always present
      
      // Evidence pages calculation (matching puppeteerPDFService logic)
      let evidencePages = 0;
      if (siteNotesData?.formData?.evidencePhotos && Array.isArray(siteNotesData.formData.evidencePhotos)) {
        const evidenceCategories = [
          { id: 'exterior', label: 'Exterior' },
          { id: 'wall-construction', label: 'Wall Construction Type' },
          { id: 'wall-insulation', label: 'Wall Insulation' },
          { id: 'floor', label: 'Floor' },
          { id: 'windows-doors', label: 'Windows & Doors' },
          { id: 'heating-system', label: 'Heating System' },
          { id: 'ventilation', label: 'Ventilation' },
          { id: 'lighting', label: 'Lighting' },
          { id: 'other', label: 'Other Evidence' }
        ];
        
        evidenceCategories.forEach(category => {
          const categoryPhotos = (siteNotesData.formData.evidencePhotos || []).filter((p: any) => p.category === category.id);
          if (categoryPhotos.length > 0) {
            // MUST match puppeteerPDFService line ~3530: 4 photos per page
            const photosPerPage = 4;
            const pagesForCategory = Math.ceil(categoryPhotos.length / photosPerPage);
            evidencePages += pagesForCategory;
          }
        });
      }
      
      // Offline export goes straight to floor plans (no Property Overview page like puppeteer export)
      totalPages = totalFloorPlanPages + calculationsPages + contentPages + evidencePages;
      
      console.log(`📊 TOTAL PAGES CALCULATION: ${totalFloorPlanPages} floor plan + ${calculationsPages} calculations + ${contentPages} content + ${evidencePages} evidence = ${totalPages} total pages`);
      console.log(`📊 Floor page counts:`, floorPageCounts);
      
      console.log(`📄 [PAGE_NUMBER_TRACKING] ======== STARTING PAGE NUMBER EMBEDDING ========`);
      console.log(`📄 [PAGE_NUMBER_TRACKING] Initial totalPages variable: ${totalPages}`);
      
      // Generate a page for each floor with actual floor plan data (matching jsPDF exportComprehensivePNG logic)
      for (let i = 0; i < availableFloors.length; i++) {
        const floorNumber = availableFloors[i];
        // Find corresponding site notes floor data
        const floor = siteNotesData.floors.find(f => f.id === floorNumber) || {
          id: floorNumber,
          floorLevel: floorNumber === 1 ? 'ground floor' : `floor ${floorNumber}`,
          buildingType: 'main building',
          height: '',
          floorArea: '',
          hlp: '',
          pw: '',
          rooms: []
        };
        
        // Get floor-specific data (matching exportUtils.ts logic exactly)
        const floorDataFromAll = allFloorData[floorNumber];
        const floorWalls = floorDataFromAll?.walls || (floorNumber === data.currentFloor ? currentWalls : allFloorPlans[floorNumber] || []);
        const floorElements = floorDataFromAll?.elements || elements.filter((el: any) => el.floor === floorNumber);
        
        // 🎯 SIMPLIFIED ROOM ASSIGNMENT: Trust the wall floor assignments from canvas
        let floorRooms;
        if (floorDataFromAll?.rooms) {
          // Use pre-calculated floor data if available
          floorRooms = floorDataFromAll.rooms;
          console.log(`📍 FLOOR ${floorNumber}: Using pre-calculated rooms (${floorRooms.length} rooms)`);
        } else {
          // Simple approach: Room belongs to floor where majority of its walls are
          floorRooms = rooms.filter((room: any) => {
            // If room has walls, use wall floor assignments
            if (room.walls && room.walls.length > 0) {
              const wallsOnThisFloor = room.walls.filter((wall: any) => wall.floor === floorNumber);
              const isOnThisFloor = wallsOnThisFloor.length > 0;
              
              if (isOnThisFloor) {
                console.log(`✅ Room ${room.name || room.id} assigned to Floor ${floorNumber} (${wallsOnThisFloor.length}/${room.walls.length} walls)`);
              }
              
              return isOnThisFloor;
            }
            
            // If room has no wall data, skip it (better than guessing)
            console.log(`⚠️ Room ${room.name || room.id} has no wall data - skipping`);
            return false;
          });
          
          console.log(`📍 FLOOR ${floorNumber}: Assigned ${floorRooms.length} rooms based on wall floor properties`);
        }
        
        // 🚨 DEEP DEBUG: Log comprehensive room data
        console.log(`🚨 DEEP ROOM DEBUG Floor ${floorNumber}:`, {
          beforeFilteringCount: rooms.length,
          afterFilteringCount: floorRooms?.length || 0,
          isCurrentFloor: floorNumber === data.currentFloor,
          currentFloor: data.currentFloor,
          hasFloorDataFromAll: !!floorDataFromAll,
          hasFloorDataRooms: !!floorDataFromAll?.rooms,
          floorDataRoomsCount: floorDataFromAll?.rooms?.length || 0,
          originalRoomNames: rooms.map((r: any) => r.name || 'unnamed'),
          filteredRoomNames: floorRooms?.map((r: any) => r.name || 'unnamed') || [],
          filteredRoomIds: floorRooms?.map((r: any) => r.id || r.stableId) || [],
          method: floorDataFromAll?.rooms ? 'pre-defined' : 
                  (floorNumber === data.currentFloor ? 'current-floor' : 'spatial-fallback')
        });
        
        const floorTextBoxes = floorDataFromAll?.textBoxes || (floorNumber === data.currentFloor ? textBoxes : []);
        const floorMetrics = floorDataFromAll?.floorMetrics || { hlpLength: 0, pwLength: 0, totalArea: 0 };
        
        console.log(`📄 SERVER PDF Floor ${floorNumber} data:`, {
          walls: floorWalls?.length || 0,
          elements: floorElements?.length || 0,
          rooms: floorRooms?.length || 0,
          textBoxes: floorTextBoxes?.length || 0,
          hasFloorData: floorWalls.length > 0 || floorElements.length > 0,
          extractionMethod: 'jsPDF-compatible floor-specific data extraction'
        });
        
        // Skip floors with no meaningful content (matching exportUtils.ts)
        if (floorWalls.length === 0 && floorElements.length === 0) {
          console.log(`Skipping floor ${floorNumber} - no content`);
          continue;
        }
        
        // Generate room label positions ONLY for rooms that belong to this floor
        const floorSpecificRoomLabelPositions: Record<string, Point> = {};
        if (floorRooms && floorRooms.length > 0) {
          // Create a set of room IDs that belong to this floor for validation
          const floorRoomIds = new Set(floorRooms.map((room: any) => room.stableId || room.id));
          
          // 🔧 CRITICAL FIX: Filter ALL room label positions to only include this floor's rooms
          console.log(`🏷️ FLOOR ${floorNumber} LABEL ISOLATION - Filtering room labels:`, {
            totalRoomLabelPositions: data.roomLabelPositions ? Object.keys(data.roomLabelPositions).length : 0,
            floorRoomIds: Array.from(floorRoomIds),
            floorRoomsCount: floorRooms.length
          });
          
          floorRooms.forEach(room => {
            const roomKey = room.stableId || room.id;
            if (roomKey && floorRoomIds.has(roomKey)) {
              let position: Point | null = null;
              
              // Only use canvas positions if the room actually belongs to this floor
              if (data.roomLabelPositions && data.roomLabelPositions[roomKey]) {
                position = data.roomLabelPositions[roomKey];
                console.log(`✅ FLOOR ${floorNumber}: Using canvas position for FLOOR-SPECIFIC room ${roomKey}:`, position);
              } 
              // Use room center as fallback only if no canvas position exists
              else if (room.center) {
                position = room.center;
                console.log(`📍 FLOOR ${floorNumber}: Using room center for ${roomKey}:`, position);
              }
              
              // CRITICAL: Only add position if we found a valid one for this specific floor
              if (position) {
                floorSpecificRoomLabelPositions[roomKey] = position;
              }
            } else {
              console.log(`❌ FLOOR ${floorNumber}: Skipping room ${roomKey} - not assigned to this floor`);
            }
          });
          
          // 🔧 ADDITIONAL SAFEGUARD: Remove any stray positions that don't belong to this floor
          const validatedPositions: Record<string, Point> = {};
          Object.entries(floorSpecificRoomLabelPositions).forEach(([roomKey, position]) => {
            if (floorRoomIds.has(roomKey)) {
              validatedPositions[roomKey] = position;
            } else {
              console.log(`🚫 FLOOR ${floorNumber}: Removed stray position for room ${roomKey} not on this floor`);
            }
          });
          
          // Replace with validated positions
          Object.assign(floorSpecificRoomLabelPositions, validatedPositions);
          console.log(`✅ FLOOR ${floorNumber} FINAL LABEL COUNT: ${Object.keys(floorSpecificRoomLabelPositions).length} positions validated`);
        }

        console.log(`🏢 OFFLINE Floor ${floorNumber} room label positions:`, {
          totalPositions: Object.keys(floorSpecificRoomLabelPositions).length,
          roomCount: floorRooms?.length || 0,
          availablePositions: Object.keys(floorSpecificRoomLabelPositions),
          isCurrentFloor: floorNumber === data.currentFloor,
          floorRoomIds: floorRooms?.map((r: any) => r.stableId || r.id) || [],
          floorRoomNames: floorRooms?.map((r: any) => r.name || 'unnamed') || []
        });

        // 🔧 DEBUG: Log detailed room-floor assignments for troubleshooting
        console.log(`🔍 FLOOR ${floorNumber} ROOM DETAILS:`, floorRooms?.map((room: any) => ({
          id: room.stableId || room.id,
          name: room.name || 'unnamed',
          wallCount: room.walls?.length || 0,
          wallFloors: room.walls?.map((w: any) => w.floor).filter((f: any) => f !== undefined) || [],
          hasPosition: !!(floorSpecificRoomLabelPositions[room.stableId || room.id])
        })) || []);

        // 🚨 CRITICAL DEBUG: Log room assignments before processing
        console.log(`🚨 FLOOR ${floorNumber} ROOM ASSIGNMENTS BEFORE PROCESSING:`, {
          totalOriginalRooms: rooms.length,
          floorRoomsCount: floorRooms?.length || 0,
          floorRoomNames: floorRooms?.map((r: any) => r.name || 'unnamed') || [],
          floorRoomIds: floorRooms?.map((r: any) => r.stableId || r.id) || []
        });

        // 🔧 CRITICAL FIX: Filter custom labels to ONLY include this floor's rooms
        const floorSpecificCustomLabels: Record<string, string> = {};
        if (data.roomCustomLabels && floorRooms) {
          const floorRoomIds = new Set(floorRooms.map((room: any) => room.stableId || room.id));
          
          Object.entries(data.roomCustomLabels).forEach(([roomKey, label]) => {
            if (floorRoomIds.has(roomKey)) {
              floorSpecificCustomLabels[roomKey] = label;
              console.log(`✅ FLOOR ${floorNumber}: Including custom label for room ${roomKey}: "${label}"`);
            } else {
              console.log(`❌ FLOOR ${floorNumber}: Excluding custom label for room ${roomKey}: "${label}" (not on this floor)`);
            }
          });
        }
        
        console.log(`🏷️ FLOOR ${floorNumber} LABEL FILTERING:`, {
          totalCustomLabels: data.roomCustomLabels ? Object.keys(data.roomCustomLabels).length : 0,
          floorSpecificLabels: Object.keys(floorSpecificCustomLabels).length,
          floorRoomCount: floorRooms?.length || 0
        });

        // Apply ONLY floor-specific custom labels to rooms before PDF generation
        const roomsWithCustomLabels = floorRooms?.map((room: any) => {
          const roomKey = room.stableId || room.id;
          const customLabel = roomKey && floorSpecificCustomLabels[roomKey];
          
          if (customLabel && customLabel !== room.name) {
            console.log(`🏷️ FLOOR ${floorNumber}: Applying FLOOR-SPECIFIC custom label for room ${roomKey}: "${room.name}" → "${customLabel}"`);
            return {
              ...room,
              name: customLabel // Override internal name with custom label
            };
          }
          return room;
        }) || [];

        // 🚨 CRITICAL DEBUG: Verify final room data being passed to canvas
        console.log(`🚨 FLOOR ${floorNumber} FINAL CANVAS DATA:`, {
          roomsCount: roomsWithCustomLabels.length,
          roomNames: roomsWithCustomLabels.map((r: any) => r.name || 'unnamed'),
          roomIds: roomsWithCustomLabels.map((r: any) => r.stableId || r.id),
          positionKeys: Object.keys(floorSpecificRoomLabelPositions),
          positionEntries: Object.entries(floorSpecificRoomLabelPositions).map(([key, pos]) => ({ 
            key, 
            x: pos.x?.toFixed(1), 
            y: pos.y?.toFixed(1) 
          }))
        });

        // 🚨 KEY MATCHING DEBUG: Check if room keys match position keys
        const roomKeys = roomsWithCustomLabels.map((r: any) => r.stableId || r.id);
        const posKeys = Object.keys(floorSpecificRoomLabelPositions);
        const matchingKeys = roomKeys.filter(key => posKeys.includes(key));
        const missingPositions = roomKeys.filter(key => !posKeys.includes(key));
        const extraPositions = posKeys.filter(key => !roomKeys.includes(key));
        
        console.log(`🔍 FLOOR ${floorNumber} KEY MATCHING ANALYSIS:`, {
          totalRooms: roomKeys.length,
          totalPositions: posKeys.length,
          matchingKeys: matchingKeys.length,
          missingPositions,
          extraPositions,
          perfectMatch: missingPositions.length === 0 && extraPositions.length === 0
        });

        // Create floor-specific export data (matching exportUtils.ts floorExportData structure)
        const floorExportData = {
          ...data,
          currentWalls: floorWalls,
          elements: floorElements,
          rooms: roomsWithCustomLabels,
          textBoxes: floorTextBoxes,
          currentFloor: floorNumber,
          hlpLength: floorMetrics.hlpLength,
          pwLength: floorMetrics.pwLength,
          totalArea: floorMetrics.totalArea,
          roomLabelPositions: floorSpecificRoomLabelPositions, // Add room label positions for this floor
        };

        const canvas = await generateEnhancedFloorPlanCanvas(floorExportData, universalMaxWidth, universalMaxHeight);
        if (canvas) {
          // 🔍 DEBUG: Floor plan dimensions and PDF fit analysis
          const A4_WIDTH_MM = 210;
          const A4_HEIGHT_MM = 297;
          const AVAILABLE_WIDTH_MM = A4_WIDTH_MM - 10; // 5mm padding on each side
          
          // Standard single-page layout with element key below floor plan
          const STANDARD_PAGE_HEIGHT_MM = 230; // Max height from CSS (.floorplan-image max-height)
          const FULL_PAGE_HEIGHT_MM = 270; // Floor plan uses almost entire page when split
          
          const MM_TO_PX = 3.7795; // Conversion factor at 96 DPI
          
          const canvasWidthPx = canvas.width;
          const canvasHeightPx = canvas.height;
          const canvasWidthMm = canvasWidthPx / MM_TO_PX;
          const canvasHeightMm = canvasHeightPx / MM_TO_PX;
          
          // Calculate how floor plan fits in standard layout (with element key on same page)
          const standardWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
          const standardHeightScale = STANDARD_PAGE_HEIGHT_MM / canvasHeightMm;
          const standardScale = Math.min(standardWidthScale, standardHeightScale, 1);
          
          const standardFinalWidthMm = canvasWidthMm * standardScale;
          const standardFinalHeightMm = canvasHeightMm * standardScale;
          
          const isCompressed = standardScale < 1;
          const compressionPercent = ((1 - standardScale) * 100).toFixed(1);
          
          // Also calculate full-page metrics for comparison
          const fullPageWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
          const fullPageHeightScale = FULL_PAGE_HEIGHT_MM / canvasHeightMm;
          const fullPageScale = Math.min(fullPageWidthScale, fullPageHeightScale, 1);
          const fullPageCompressionPercent = ((1 - fullPageScale) * 100).toFixed(1);
          
          // Use standard page metrics for display
          const finalScale = standardScale;
          const finalWidthMm = standardFinalWidthMm;
          const finalHeightMm = standardFinalHeightMm;
          
          // Check for aspect ratio distortion (stretching)
          const scaleDifference = Math.abs(standardWidthScale - standardHeightScale);
          const scaleRatio = standardWidthScale / standardHeightScale;
          const wouldBeStretched = scaleRatio > 1.2 || scaleRatio < 0.8; // 20% aspect ratio tolerance
          
          console.log(`📐 FLOOR PLAN SIZE DEBUG - Floor ${floorNumber}:`, {
            canvasDimensions: {
              widthPx: canvasWidthPx,
              heightPx: canvasHeightPx,
              widthMm: canvasWidthMm.toFixed(1),
              heightMm: canvasHeightMm.toFixed(1),
            },
            standardPageConstraints: {
              availableWidthMm: AVAILABLE_WIDTH_MM,
              availableHeightMm: STANDARD_PAGE_HEIGHT_MM,
            },
            standardPageScaling: {
              widthScale: standardWidthScale.toFixed(3),
              heightScale: standardHeightScale.toFixed(3),
              finalScale: standardScale.toFixed(3),
              scaleDifference: scaleDifference.toFixed(3),
              scaleRatio: scaleRatio.toFixed(3),
              limitingFactor: standardWidthScale < standardHeightScale ? 'WIDTH' : 'HEIGHT',
            },
            standardPageFit: {
              fitsWithoutCompression: !isCompressed,
              isCompressed: isCompressed,
              compressionPercent: isCompressed ? compressionPercent + '%' : 'NONE',
              finalWidthMm: finalWidthMm.toFixed(1),
              finalHeightMm: finalHeightMm.toFixed(1),
              wouldBeStretched: wouldBeStretched,
            },
            fullPageComparison: {
              compressionPercent: fullPageCompressionPercent + '%',
              wouldFitBetterOnFullPage: fullPageScale > standardScale,
            }
          });
          
          const imgData = canvas.toDataURL('image/png', 1.0);
          const bestAddress = getBestAddress(data.propertyAddress, siteNotesData?.formData?.address);
          
          // Minimalistic, properly capitalized floor title
          const floorLevel = floor.floorLevel === 'ground' ? 'Ground Floor' : 
                            floor.floorLevel === 'first' ? 'First Floor' :
                            floor.floorLevel === 'second' ? 'Second Floor' :
                            floor.floorLevel === 'third' ? 'Third Floor' :
                            floor.floorLevel === 'fourth' ? 'Fourth Floor' :
                            (floor.floorLevel || 'Floor').split(' ').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
          
          const buildingType = floor.buildingType === 'main building' ? 'Main Building' : 
                              (floor.buildingType || '').split(' ').map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
          
          const floorName = buildingType ? `${floorLevel} • ${buildingType}` : floorLevel;
          
          const logoHtml = options.logoDataUrl ? `<img src="${options.logoDataUrl}" alt="Company Logo" class="header-logo" />` : '';
          const companyDetailsHtml = (options.companyName || options.companyWebsite || options.companyPhone) 
            ? `<p style="font-size: 8pt; color: #64748b; margin: 2pt 0 0 0;">${[options.companyName, options.companyWebsite, options.companyPhone].filter(Boolean).join(' • ')}</p>`
            : '';
          console.log(`🖼️ LOGO DEBUG: Floor ${floorNumber} header HTML:`, {
            hasLogo: !!options.logoDataUrl,
            logoHtmlLength: logoHtml.length,
            logoHtml: logoHtml.substring(0, 150) + '...'
          });
          
          // 🎯 DECISION: Use full-page layout ONLY if floor plan would be stretched/distorted
          // We don't care about compression (scaling down), only stretching (aspect ratio distortion)
          // Split pages when width/height scales are too different (would cause stretching)
          const needsFullPage = wouldBeStretched;
          
          if (needsFullPage) {
            console.log(`📄 FULL-PAGE LAYOUT: Floor ${floorNumber} needs full page (${compressionPercent} compression)`);
            console.log(`📄 [PAGE_NUMBER_EMBED] Floor ${floorNumber} page 1: Will embed "Page ${currentPageNum} of ${totalPages}"`);
            console.log(`📄 [PAGE_NUMBER_EMBED] Floor ${floorNumber} page 2: Will embed "Page ${currentPageNum + 1} of ${totalPages}"`);
            
            // Page 1: Full-page floor plan with Building Fabric table at top
            floorPlanSection += `
              <div class="pdf-page ${i === 0 ? 'first-page' : ''}">
                <div class="header">
                  <div class="header-content">
                    <div class="header-left">
                      <h1 class="property-address">${bestAddress}</h1>
                      ${companyDetailsHtml}
                      <span class="page-number">Page ${currentPageNum} of ${totalPages}</span>
                    </div>
                    <div class="header-right">
                      ${logoHtml}
                    </div>
                  </div>
                </div>

                <div class="header-separator"></div>

                <div class="content-section floorplan-section">
                  <h2 class="section-heading">${floorName}</h2>
                  ${generateBuildingFabricTableHTML(siteNotesData)}
                  <img class="floorplan-image-fullpage" src="${imgData}" alt="${floorName}" />
                </div>
              </div>`;
            
            // Page 2: Element key on separate page (Building Fabric now on page 1)
            floorPlanSection += `
              <div class="pdf-page">
                <div class="header">
                  <div class="header-content">
                    <div class="header-left">
                      <h1 class="property-address">${bestAddress}</h1>
                      ${companyDetailsHtml}
                      <p class="section-title">${floorName} - Legend</p>
                      <span class="page-number">Page ${currentPageNum + 1} of ${totalPages}</span>
                    </div>
                    <div class="header-right">
                      ${logoHtml}
                    </div>
                  </div>
                </div>

                <div class="header-separator"></div>

                <div class="content-section">
                  ${getElementKeyHTML()}
                </div>
              </div>`;
            console.log(`📄 [PDF_PAGES_OFFLINE] Floor ${floorNumber} using FULL-PAGE layout (2 pages)`);
            currentPageNum += 2; // Full-page layout uses 2 pages
            console.log(`📄 [PDF_PAGES_OFFLINE] Current page now: ${currentPageNum}`);
          } else {
            console.log(`📄 STANDARD LAYOUT: Floor ${floorNumber} fits on one page (${compressionPercent} compression)`);
            
            // Standard layout: everything on one page
            floorPlanSection += `
              <div class="pdf-page ${i === 0 ? 'first-page' : ''}">
                <div class="header">
                  <div class="header-content">
                    <div class="header-left">
                      <h1 class="property-address">${bestAddress}</h1>
                      ${companyDetailsHtml}
                      <p class="section-title">${floorName}</p>
                      <span class="page-number">Page ${currentPageNum} of ${totalPages}</span>
                    </div>
                    <div class="header-right">
                      ${logoHtml}
                    </div>
                  </div>
                </div>

                <div class="header-separator"></div>

                <div class="content-section floorplan-section">
                  <h2 class="section-heading">${floorName}</h2>
                  ${generateBuildingFabricTableHTML(siteNotesData)}
                  <img class="floorplan-image" src="${imgData}" alt="${floorName}" />
                  ${getElementKeyHTML()}
                </div>
              </div>`;
            console.log(`📄 [PDF_PAGES_OFFLINE] Floor ${floorNumber} using STANDARD layout (1 page)`);
            console.log(`📄 [PAGE_NUMBER_EMBED] Floor ${floorNumber} standard: Will embed "Page ${currentPageNum} of ${totalPages}"`);
            currentPageNum += 1; // Standard layout uses 1 page
            console.log(`📄 [PDF_PAGES_OFFLINE] Current page now: ${currentPageNum}`);
          }
        } else {
          console.warn(`❌ SERVER PDF: Failed to generate canvas for floor ${floorNumber}`);
        }
      }
    } else {
      // Fallback to single floor if no floor data
      console.log('📄 SERVER PDF: No floor data, generating single floor plan');
      const canvas = await generateEnhancedFloorPlanCanvas(data);
      if (canvas) {
        // 🔍 DEBUG: Floor plan dimensions and PDF fit analysis (Fallback Single Floor)
        const A4_WIDTH_MM = 210;
        const A4_HEIGHT_MM = 297;
        const AVAILABLE_WIDTH_MM = A4_WIDTH_MM - 10; // 5mm padding on each side
        
        // Standard single-page layout with element key below floor plan
        const STANDARD_PAGE_HEIGHT_MM = 230; // Max height from CSS (.floorplan-image max-height)
        const FULL_PAGE_HEIGHT_MM = 270; // Floor plan uses almost entire page when split
        
        const MM_TO_PX = 3.7795; // Conversion factor at 96 DPI
        
        const canvasWidthPx = canvas.width;
        const canvasHeightPx = canvas.height;
        const canvasWidthMm = canvasWidthPx / MM_TO_PX;
        const canvasHeightMm = canvasHeightPx / MM_TO_PX;
        
        // Calculate how floor plan fits in standard layout (with element key on same page)
        const standardWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
        const standardHeightScale = STANDARD_PAGE_HEIGHT_MM / canvasHeightMm;
        const standardScale = Math.min(standardWidthScale, standardHeightScale, 1);
        
        const standardFinalWidthMm = canvasWidthMm * standardScale;
        const standardFinalHeightMm = canvasHeightMm * standardScale;
        
        const isCompressed = standardScale < 1;
        const compressionPercent = ((1 - standardScale) * 100).toFixed(1);
        
        // Also calculate full-page metrics for comparison
        const fullPageWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
        const fullPageHeightScale = FULL_PAGE_HEIGHT_MM / canvasHeightMm;
        const fullPageScale = Math.min(fullPageWidthScale, fullPageHeightScale, 1);
        const fullPageCompressionPercent = ((1 - fullPageScale) * 100).toFixed(1);
        
        // Use standard page metrics for display
        const finalScale = standardScale;
        const finalWidthMm = standardFinalWidthMm;
        const finalHeightMm = standardFinalHeightMm;
        
        // Check for aspect ratio distortion (stretching)
        const scaleDifference = Math.abs(standardWidthScale - standardHeightScale);
        const scaleRatio = standardWidthScale / standardHeightScale;
        const wouldBeStretched = scaleRatio > 1.2 || scaleRatio < 0.8; // 20% aspect ratio tolerance
        
        console.log('📐 FLOOR PLAN SIZE DEBUG - Single Floor (Fallback):', {
          canvasDimensions: {
            widthPx: canvasWidthPx,
            heightPx: canvasHeightPx,
            widthMm: canvasWidthMm.toFixed(1),
            heightMm: canvasHeightMm.toFixed(1),
          },
          standardPageConstraints: {
            availableWidthMm: AVAILABLE_WIDTH_MM,
            availableHeightMm: STANDARD_PAGE_HEIGHT_MM,
          },
          standardPageScaling: {
            widthScale: standardWidthScale.toFixed(3),
            heightScale: standardHeightScale.toFixed(3),
            finalScale: standardScale.toFixed(3),
            scaleDifference: scaleDifference.toFixed(3),
            scaleRatio: scaleRatio.toFixed(3),
            limitingFactor: standardWidthScale < standardHeightScale ? 'WIDTH' : 'HEIGHT',
          },
          standardPageFit: {
            fitsWithoutCompression: !isCompressed,
            isCompressed: isCompressed,
            compressionPercent: isCompressed ? compressionPercent + '%' : 'NONE',
            finalWidthMm: finalWidthMm.toFixed(1),
            finalHeightMm: finalHeightMm.toFixed(1),
            wouldBeStretched: wouldBeStretched,
          },
          fullPageComparison: {
            compressionPercent: fullPageCompressionPercent + '%',
            wouldFitBetterOnFullPage: fullPageScale > standardScale,
          }
        });
        
        const imgData = canvas.toDataURL('image/png', 1.0);
        const bestAddress = getBestAddress(data.propertyAddress, (data as any)?.siteNotesData?.formData?.address);
        const fallbackLogoHtml = options.logoDataUrl ? `<img src="${options.logoDataUrl}" alt="Company Logo" class="header-logo" />` : '';
        const fallbackCompanyDetailsHtml = (options.companyName || options.companyWebsite || options.companyPhone) 
          ? `<p style="font-size: 8pt; color: #64748b; margin: 2pt 0 0 0;">${[options.companyName, options.companyWebsite, options.companyPhone].filter(Boolean).join(' • ')}</p>`
          : '';
        console.log('🖼️ LOGO DEBUG: Fallback single floor header HTML:', {
          hasLogo: !!options.logoDataUrl,
          logoHtmlLength: fallbackLogoHtml.length,
          logoHtml: fallbackLogoHtml.substring(0, 150) + '...'
        });
        
        // 🎯 DECISION: Use full-page layout ONLY if floor plan would be stretched/distorted
        // We don't care about compression (scaling down), only stretching (aspect ratio distortion)
        // Split pages when width/height scales are too different (would cause stretching)
        const needsFullPage = wouldBeStretched;
        
        if (needsFullPage) {
          console.log(`📄 FULL-PAGE LAYOUT: Single floor needs full page (${compressionPercent} compression)`);
          console.log(`📄 [PAGE_NUMBER_EMBED] Fallback page 1: Will embed "Page ${currentPageNum} of ${totalPages}"`);
          console.log(`📄 [PAGE_NUMBER_EMBED] Fallback page 2: Will embed "Page ${currentPageNum + 1} of ${totalPages}"`);
          console.log(`📄 [PAGE_NUMBER] Fallback: After page 1, incrementing currentPageNum from ${currentPageNum} to ${currentPageNum + 2}`);
          currentPageNum += 2;
          // Don't overwrite totalPages - it was correctly calculated earlier
          
          // Page 1: Full-page floor plan
          floorPlanSection = `
            <div class="pdf-page first-page">
              <div class="header">
                <div class="header-content">
                  <div class="header-left">
                    <h1 class="property-address">${bestAddress}</h1>
                    ${fallbackCompanyDetailsHtml}
                    <p class="section-title">Floor Plan</p>
                    <span class="page-number">Page ${currentPageNum} of ${totalPages}</span>
                  </div>
                  <div class="header-right">
                    ${fallbackLogoHtml}
                  </div>
                </div>
              </div>

              <div class="header-separator"></div>

              <div class="content-section floorplan-section">
                <img class="floorplan-image-fullpage" src="${imgData}" alt="Detailed Floor Plan" />
              </div>
            </div>`;
          // Page 2: Element key
          floorPlanSection += `
            <div class="pdf-page">
              <div class="header">
                <div class="header-content">
                  <div class="header-left">
                    <h1 class="property-address">${bestAddress}</h1>
                    ${fallbackCompanyDetailsHtml}
                    <p class="section-title">Floor Plan - Legend</p>
                    <span class="page-number">Page ${currentPageNum + 1} of ${totalPages}</span>
                  </div>
                  <div class="header-right">
                    ${fallbackLogoHtml}
                  </div>
                </div>
              </div>

              <div class="header-separator"></div>

              <div class="content-section">
                ${getElementKeyHTML()}
              </div>
            </div>`;
        } else {
          console.log(`📄 STANDARD LAYOUT: Single floor fits on one page (${compressionPercent} compression)`);
          
          // Standard layout: everything on one page
          floorPlanSection = `
            <div class="pdf-page first-page">
              <div class="header">
                <div class="header-content">
                  <div class="header-left">
                    <h1 class="property-address">${bestAddress}</h1>
                    ${fallbackCompanyDetailsHtml}
                    <p class="section-title">Floor Plan</p>
                    <span class="page-number">Page ${currentPageNum} of ${totalPages}</span>
                  </div>
                  <div class="header-right">
                    ${fallbackLogoHtml}
                  </div>
                </div>
              </div>

              <div class="header-separator"></div>

              <div class="content-section floorplan-section">
                <h2 class="section-heading">Detailed Floor Plan</h2>
                <img class="floorplan-image" src="${imgData}" alt="Detailed Floor Plan" />
                ${getElementKeyHTML()}
              </div>
            </div>`;
          currentPageNum += 1;
          // Don't overwrite totalPages - it was correctly calculated earlier
        }
      } else {
        console.warn('❌ SERVER PDF: Failed to generate canvas for single floor fallback');
      }
    }
  } catch (err) {
    console.warn('Could not render floor plan canvas for server PDF:', err);
  }

  // Use the filename parameter passed to the function (with timestamp if default)
  if (filename === 'property-report.pdf') {
    const timestamp = new Date().toISOString().split('T')[0];
    filename = `property-assessment-${timestamp}.pdf`;
  }

  // 3) Generate Formula & Working content with detailed calculations (matching server version)
  const generateFormulaWorkingContent = (data: ExportData, address: string, startingPageNum: number, totalPages: number, logoDataUrl?: string, companyName?: string, companyWebsite?: string, companyPhone?: string): { firstPageContent: string; additionalPages: string } => {
    const { siteNotesData, allFloorData } = data;
    if (!siteNotesData?.floors) return { firstPageContent: '', additionalPages: '' };
    
    // Helper functions from server logic
    const estimateRectangleDimensions = (area: number): { width: number; height: number } => {
      const ratio = 1.618; // Golden ratio
      const width = Math.sqrt(area * ratio);
      const height = area / width;
      return { width, height };
    };

    const estimateWallSegments = (totalLength: number, type: string): number[] => {
      if (totalLength <= 0) return [];
      const segmentCount = Math.max(2, Math.min(6, Math.ceil(totalLength / 3)));
      const baseSegment = totalLength / segmentCount;
      const segments: number[] = [];
      
      for (let i = 0; i < segmentCount; i++) {
        const variation = (Math.random() - 0.5) * 0.4;
        segments.push(Math.max(0.5, baseSegment + variation));
      }
      
      const actualTotal = segments.reduce((sum, seg) => sum + seg, 0);
      const scaleFactor = totalLength / actualTotal;
      return segments.map(seg => parseFloat((seg * scaleFactor).toFixed(2)));
    };

    const generateAreaCalculation = (floor: any, floorArea: number, siteNotesData: any): string => {
      const validRooms = floor.rooms?.filter((room: any) => {
        const hasValidName = room.name && room.name.trim() !== '';
        const length = parseFloat(room.length) || 0;
        const width = parseFloat(room.width) || 0;
        const area = parseFloat(room.area) || 0;
        const hasDimensions = length > 0 && width > 0;
        const hasAreaValue = area > 0;
        return hasValidName && (hasDimensions || hasAreaValue);
      }) || [];

      // Handle highlighter extensions or no rooms
      if (validRooms.length === 0 && floorArea > 0) {
        if (floor.highlighterBreakdown?.dimensions) {
          const { width, height } = floor.highlighterBreakdown.dimensions;
          return `${width.toFixed(2)}m × ${height.toFixed(2)}m = ${floorArea.toFixed(2)}m²`;
        } else {
          const estimatedDimensions = estimateRectangleDimensions(floorArea);
          return `${estimatedDimensions.width.toFixed(2)}m × ${estimatedDimensions.height.toFixed(2)}m = ${floorArea.toFixed(2)}m²`;
        }
      }
      
      // Handle multiple rooms
      if (validRooms.length > 1) {
        const roomCalcs = validRooms.map((room: any) => {
          const length = parseFloat(room.length) || 0;
          const width = parseFloat(room.width) || 0;
          const area = parseFloat(room.area) || 0;
          
          if (length > 0 && width > 0) {
            const calculatedArea = length * width;
            return `${length.toFixed(2)}m×${width.toFixed(2)}m=${calculatedArea.toFixed(2)}m²`;
          } else if (area > 0) {
            return `${area.toFixed(2)}m²`;
          }
          return '0.00m²';
        });

        // Check for extension deductions
        const isMainBuilding = floor.buildingType === 'main building';
        if (isMainBuilding && siteNotesData?.floors) {
          const extensionFloors = siteNotesData.floors.filter((f: any) =>
            f.floorLevel === floor.floorLevel && 
            f.buildingType !== 'main building' &&
            f.floorArea && f.floorArea.trim() !== ''
          );
        
          if (extensionFloors.length > 0) {
            let extensionDeductions = 0;
            const extensionDetails: string[] = [];

            extensionFloors.forEach((extFloor: any) => {
              const extArea = parseFloat(extFloor.floorArea) || 0;
              if (extArea > 0) {
                extensionDeductions += extArea;
                extensionDetails.push(`${extArea.toFixed(2)}m² (${extFloor.buildingType})`);
              }
            });
            
            if (extensionDeductions > 0) {
              const baseCalc = `${roomCalcs.join(' + ')}`;
              const deductionText = extensionDetails.length > 1 
                ? `${extensionDeductions.toFixed(2)}m² (extensions)`
                : extensionDetails[0];
              return `${baseCalc} - ${deductionText} = ${floorArea.toFixed(2)}m²`;
            }
          }
        }
        
        return `${roomCalcs.join(' + ')} = ${floorArea.toFixed(2)}m²`;
      }
      
      // Handle single room
      if (validRooms.length === 1) {
        const room = validRooms[0];
        const length = parseFloat(room.length) || 0;
        const width = parseFloat(room.width) || 0;
        const area = parseFloat(room.area) || 0;
        
        if (length > 0 && width > 0) {
          return `${length.toFixed(2)}m × ${width.toFixed(2)}m = ${floorArea.toFixed(2)}m²`;
        } else if (area > 0) {
          return `${area.toFixed(2)}m²`;
        }
      }
      
      return floorArea > 0 ? `${floorArea.toFixed(2)}m²` : '';
    };

    const generateHLPCalculation = (floor: any, hlp: number): string => {
      if (floor.highlighterBreakdown?.hlpSegments && floor.highlighterBreakdown.hlpSegments.length > 0) {
        const segments = floor.highlighterBreakdown.hlpSegments.map((s: number) => `${s.toFixed(2)}m`);
        return `${segments.join(' + ')} = ${hlp.toFixed(2)}m`;
      } else {
        const hlpSegments = estimateWallSegments(hlp, 'HLP');
        return `${hlpSegments.map(s => `${s.toFixed(2)}m`).join(' + ')} = ${hlp.toFixed(2)}m`;
      }
    };

    const generatePWCalculation = (floor: any, pw: number): string => {
      if (floor.highlighterBreakdown?.pwSegments && floor.highlighterBreakdown.pwSegments.length > 0) {
        const segments = floor.highlighterBreakdown.pwSegments.map((s: number) => `${s.toFixed(2)}m`);
        return `${segments.join(' + ')} = ${pw.toFixed(2)}m`;
      } else {
        const pwSegments = estimateWallSegments(pw, 'PW');
        return `${pwSegments.map(s => `${s.toFixed(2)}m`).join(' + ')} = ${pw.toFixed(2)}m`;
      }
    };
    
    // Generate calculation sections for all floors
    const calculationSections = siteNotesData.floors.map((floor: any) => {
      const floorNumber = floor.id;
      const floorArea = parseFloat(floor.floorArea) || 0;
      const hlp = parseFloat(floor.hlp) || 0;
      const pw = parseFloat(floor.pw) || 0;

      if (floorArea <= 0 && hlp <= 0 && pw <= 0) return '';

      // 🎯 FLOOR NAME FIX: Properly capitalize floor level from site notes data
      const floorName = floor.floorLevel 
        ? floor.floorLevel.charAt(0).toUpperCase() + floor.floorLevel.slice(1)
        : `Floor ${floorNumber}`;
      const buildingType = floor.buildingType === 'main building' ? 'Main Building' : 
                          floor.buildingType.charAt(0).toUpperCase() + floor.buildingType.slice(1);

      const areaCalc = generateAreaCalculation(floor, floorArea, siteNotesData);
      const hlpCalc = generateHLPCalculation(floor, hlp);
      const pwCalc = generatePWCalculation(floor, pw);

      return `
        <div class="floor-calculation-block">
          <div class="floor-header">${floorName} - ${buildingType}</div>
          <div class="calculation-content">
            ${areaCalc ? `
              <div class="calc-item">
                <div class="calc-label">Area:</div>
                <div class="calc-formula">${areaCalc}</div>
              </div>
            ` : ''}
            ${hlpCalc ? `
              <div class="calc-item">
                <div class="calc-label">HLP:</div>
                <div class="calc-formula">${hlpCalc}</div>
              </div>
            ` : ''}
            ${pwCalc ? `
              <div class="calc-item">
                <div class="calc-label">PW:</div>
                <div class="calc-formula">${pwCalc}</div>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }).filter(Boolean);

    if (calculationSections.length === 0) {
      return {
        firstPageContent: `
          <div class="content-section">
            <h2 class="section-heading">Formula & Working - All Floors</h2>
            <div class="note-text">No calculation data available.</div>
          </div>`,
        additionalPages: ''
      };
    }

    // 🔧 PAGINATION: Split calculation sections into separate PDF pages with max 3 floors per page
    const FLOORS_PER_PAGE = 3;
    
    console.log(`📊 PAGINATION DEBUG: Total floors: ${calculationSections.length}, FLOORS_PER_PAGE: ${FLOORS_PER_PAGE}`);
    
    // Always limit first page to max 3 floors, regardless of total count
    const firstPageCalculations = calculationSections.slice(0, FLOORS_PER_PAGE);
    const remainingCalculations = calculationSections.slice(FLOORS_PER_PAGE);
    
    console.log(`📊 PAGINATION DEBUG: First page floors: ${firstPageCalculations.length}, Remaining floors: ${remainingCalculations.length}`);
    
    const firstPageContent = `
      <div class="content-section">
        <h2 class="section-heading">Formula & Working - All Floors</h2>
        <div class="calculations-container">
          ${firstPageCalculations.join('')}
        </div>
      </div>
    `;
    
    // Create additional pages for remaining floors
    let additionalPages = '';
    if (remainingCalculations.length > 0) {
      let pageOffset = 1; // First additional page is +1 from starting page
      for (let i = 0; i < remainingCalculations.length; i += FLOORS_PER_PAGE) {
        const pageCalculations = remainingCalculations.slice(i, i + FLOORS_PER_PAGE);
        const logoHtml = logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : '';
        const companyDetailsHtml = (companyName || companyWebsite || companyPhone) 
          ? `<p style="font-size: 8pt; color: #64748b; margin: 2pt 0 0 0;">${[companyName, companyWebsite, companyPhone].filter(Boolean).join(' • ')}</p>`
          : '';
        
        const currentPageNumber = startingPageNum + pageOffset;
        console.log(`📊 PAGINATION DEBUG: Creating page ${currentPageNumber} with ${pageCalculations.length} floors`);
        
        additionalPages += `
          <div class="pdf-page">
            <div class="header">
              <div class="header-content">
                <div class="header-left">
                  <h1 class="property-address">${address}</h1>
                  ${companyDetailsHtml}
                  <p class="section-title">Formula & Working - All Floors (Cont.)</p>
                  <span class="page-number">Page ${currentPageNumber} of ${totalPages}</span>
                </div>
                <div class="header-right">
                  ${logoHtml}
                </div>
              </div>
            </div>

            <div class="header-separator"></div>

            <div class="content-section">
              <div class="calculations-container">
                ${pageCalculations.join('')}
              </div>
            </div>
          </div>
        `;
        pageOffset++;
      }
    }
    
    return { firstPageContent, additionalPages };
  };

  // Generate Formula & Working content
  const bestAddress = getBestAddress(data.propertyAddress, (data as any)?.siteNotesData?.formData?.address);
  // currentPageNum is already positioned after floor plans in the page sequence
  console.log(`📄 [PAGE_NUMBER_EMBED] Formula & Working: Will embed "Page ${currentPageNum} of ${totalPages}"`);
  const { firstPageContent, additionalPages } = generateFormulaWorkingContent(data, bestAddress, currentPageNum, totalPages, options.logoDataUrl, options.companyName, options.companyWebsite, options.companyPhone);
  
  // 🔧 COMPLETE REPLACEMENT: Replace entire Formula & Working section with only first 3 floors
  // Step 1: Create property overview with dimensions and ONLY first 3 calculation floors
  let propertyOverviewWithCalculations = baseHtml;
  
  // First add dimensions table after Property Overview heading
  propertyOverviewWithCalculations = propertyOverviewWithCalculations.replace(
    /(<div class="content-section">\s*<h2 class="section-heading">Property Overview & Floor Calculations<\/h2>)/,
    `$1${dimensionsTableSection}`
  );
  
  // Then completely replace the entire "All Floors - Dimensions & Calculations" section with only first page content
  const formulaWorkingPattern = /(<div class="content-section">\s*<h2 class="section-heading">All Floors - Dimensions & Calculations<\/h2>[\s\S]*?<\/div>\s*<\/div>)/;
  
  console.log(`📊 DEBUGGING REPLACEMENT:`);
  console.log(`📊 Pattern match test: ${formulaWorkingPattern.test(propertyOverviewWithCalculations)}`);
  console.log(`📊 First page content length: ${firstPageContent.length}`);
  console.log(`📊 First page content preview: ${firstPageContent.substring(0, 200)}...`);
  
  // 🔍 DEBUG: Check what sections actually exist in base HTML
  const sectionHeadings = propertyOverviewWithCalculations.match(/<h2 class="section-heading">[^<]*<\/h2>/g);
  console.log(`📊 AVAILABLE SECTIONS IN BASE HTML:`, sectionHeadings);
  
  if (formulaWorkingPattern.test(propertyOverviewWithCalculations)) {
    console.log(`📊 REPLACING entire Formula & Working section with first ${firstPageContent ? '3' : '0'} floors only`);
    const beforeReplacement = propertyOverviewWithCalculations.length;
    propertyOverviewWithCalculations = propertyOverviewWithCalculations.replace(formulaWorkingPattern, firstPageContent);
    const afterReplacement = propertyOverviewWithCalculations.length;
    console.log(`📊 REPLACEMENT RESULT: ${beforeReplacement} → ${afterReplacement} chars`);
  } else {
    console.log(`📊 FORMULA SECTION NOT FOUND: Adding first page content to end`);
    propertyOverviewWithCalculations += firstPageContent;
  }

  // Step 2: CLEAN APPROACH - Remove calculations from base content and add as separate pages
  // Remove the "All Floors - Dimensions & Calculations" section entirely to preserve page boundaries
  const cleanBaseHtml = baseHtml.replace(formulaWorkingPattern, '');
  
  // Keep base content clean without dimensions table (it will go to Formula & Working page)
  const baseWithDimensions = cleanBaseHtml;
  
  console.log(`📊 CLEAN SEPARATION: Base content without calculations + All calculation pages separate`);
  console.log(`📊 CLEAN SIZES: Base: ${baseWithDimensions.length}, First page: ${firstPageContent.length}, Additional: ${additionalPages.length}`);
  
  // Create a separate page for the first 3 calculations with Property Overview table as first table
  const logoHtml = options.logoDataUrl ? `<img src="${options.logoDataUrl}" alt="Company Logo" class="header-logo" />` : '';
  const firstPageCompanyDetailsHtml = (options.companyName || options.companyWebsite || options.companyPhone) 
    ? `<p style="font-size: 8pt; color: #64748b; margin: 2pt 0 0 0;">${[options.companyName, options.companyWebsite, options.companyPhone].filter(Boolean).join(' • ')}</p>`
    : '';
  const firstCalculationsPage = `
    <div class="pdf-page">
      <div class="header">
        <div class="header-content">
          <div class="header-left">
            <h1 class="property-address">${bestAddress}</h1>
            ${firstPageCompanyDetailsHtml}
            <p class="section-title">Formula & Working - All Floors</p>
            <span class="page-number">Page ${currentPageNum} of ${totalPages}</span>
          </div>
          <div class="header-right">${logoHtml}</div>
        </div>
      </div>
      <div class="header-separator"></div>
      ${dimensionsTableSection}
      ${firstPageContent}
    </div>
  `;
  
  console.log(`📊 PROPERTY TABLE MOVED: Added dimensions table to Formula & Working page`);
  
  // Split base content to insert calculations between Property Overview and Equipment Summary
  const equipmentSummaryPattern = /(<div class="pdf-page"[\s\S]*?Equipment Summary[\s\S]*)/;
  const equipmentMatch = baseWithDimensions.match(equipmentSummaryPattern);
  
  let finalHtml;
  if (equipmentMatch) {
    const [, equipmentAndAfter] = equipmentMatch;
    const beforeEquipment = baseWithDimensions.substring(0, baseWithDimensions.indexOf(equipmentAndAfter));
    
    console.log(`📊 INSERTING CALCULATIONS: Property Overview + Calculations + Equipment Summary`);
    console.log(`📊 POSITION SIZES: Before Equipment: ${beforeEquipment.length}, First calc: ${firstCalculationsPage.length}, Additional: ${additionalPages?.length || 0}, Equipment+: ${equipmentAndAfter.length}`);
    
    // Perfect order: Property Overview + Calculations + Equipment Summary + Other pages
    // Note: RIR page is already in the base HTML from puppeteerPDFService
    finalHtml = `${beforeEquipment}${firstCalculationsPage}${additionalPages || ''}${equipmentAndAfter}`;
  } else {
    console.log(`📊 NO EQUIPMENT FOUND: Adding calculations after all content`);
    finalHtml = `${baseWithDimensions}${firstCalculationsPage}${additionalPages || ''}`;
  }

  // Step 3: Build final HTML with floor plans first
  let fullHtml = `${globalStyles}${floorPlanSection}${finalHtml}`;
  
  console.log(`📊 HTML ASSEMBLY COMPLETE: Floor plans + Property overview + Additional calculations + Remaining content`);
  console.log(`📊 SECTION LENGTHS: Floor plans: ${floorPlanSection.length}, Final HTML: ${finalHtml.length}, Additional: ${additionalPages.length}`);
  
  // 🔧 CRITICAL FIX: Count ACTUAL pages and update all page numbers
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = fullHtml;
  const allPages = tempDiv.querySelectorAll('.pdf-page');
  const actualTotalPages = allPages.length;
  
  console.log(`📊 PAGE COUNT CORRECTION:`, {
    predicted: totalPages,
    actual: actualTotalPages,
    difference: actualTotalPages - totalPages,
    needsCorrection: totalPages !== actualTotalPages
  });
  
  // Replace ALL instances of "of ${predicted}" with "of ${actual}"
  if (totalPages !== actualTotalPages) {
    console.log(`⚠️ FIXING PAGE NUMBERS: Replacing "of ${totalPages}" with "of ${actualTotalPages}"`);
    fullHtml = fullHtml.replace(
      new RegExp(`of ${totalPages}`, 'g'),
      `of ${actualTotalPages}`
    );
  }
  
  console.log(`📊 FINAL HTML STRUCTURE: ${floorPlanSection ? 'Floor plan pages + ' : ''}Base content page + ${additionalPages ? 'Additional calculation pages' : 'No additional pages'}`);
  console.log(`📊 PAGES BREAKDOWN: Floor plans, Base content (first 3 calculations), Additional calculations (remaining floors)`);
  console.log(`📊 TOTAL PDF PAGES: ${actualTotalPages} (corrected from predicted ${totalPages})`);
  console.log(`📊 ADDITIONAL PAGES LENGTH: ${additionalPages.length} chars`);
  
  // 🔍 DEBUG: Log page order and content types
  Array.from(allPages).forEach((page, index) => {
    const pageTitle = page.querySelector('.section-title')?.textContent || 
                     page.querySelector('.section-heading')?.textContent || 
                     'Unknown';
    console.log(`📄 PAGE ${index + 1}: ${pageTitle}`);
  });

  // 3) Convert HTML to PDF using client-side libraries
  const blob = await convertHTMLToPDFBlob(fullHtml, filename);
  console.log('📦 PDF blob created:', { size: blob.size, type: blob.type });

  // 4) Save/Share depending on platform
  try {
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Native platform detected - using Capacitor filesystem');
      const dataUrl = await blobToBase64(blob); // e.g., "data:application/pdf;base64,...."
      const base64Data = dataUrl.split(',')[1] || dataUrl; // strip prefix if present

      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true
      });

      console.log('✅ PDF file written to filesystem:', result.uri);

      await Share.share({
        title: 'Property Assessment Report',
        text: 'Property assessment report generated on server',
        url: result.uri,
        dialogTitle: 'Share Property Assessment Report'
      });

      console.log('✅ PDF share dialog opened');
    } else {
      console.log('💻 Desktop platform detected - triggering download');
      
      // Try multiple download methods for better browser compatibility
      try {
        // Method 1: Standard download link approach with user interaction
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        console.log('🔗 Download link created:', { url, filename });
        
        // Use standard click() method - most reliable
        a.click();
        console.log('✅ Download triggered via click()');
        
        // Show user-friendly notification
        const notification = document.createElement('div');
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(to right, #10b981, #059669);
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          z-index: 10000;
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 14px;
          font-weight: 500;
          animation: slideIn 0.3s ease-out;
        `;
        notification.textContent = `✓ PDF downloaded: ${filename}`;
        document.body.appendChild(notification);
        
        // Add slide-in animation
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(400px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `;
        document.head.appendChild(style);
        
        // Remove notification after 3 seconds
        setTimeout(() => {
          notification.style.opacity = '0';
          notification.style.transform = 'translateX(400px)';
          notification.style.transition = 'all 0.3s ease-out';
          setTimeout(() => {
            document.body.removeChild(notification);
            document.head.removeChild(style);
          }, 300);
        }, 3000);
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          console.log('🧹 Cleanup completed');
        }, 1000);
        
      } catch (downloadError) {
        console.error('❌ Download failed:', downloadError);
        
        // Show error notification
        const errorMessage = downloadError instanceof Error ? downloadError.message : 'Unknown error';
        alert(`Download failed: ${errorMessage}\n\nPlease check browser permissions and try again.`);
      }
    }
  } catch (error) {
    console.error('❌ Error saving/downloading PDF:', error);
    throw error;
  }
}